/**
 * The API client. Every call goes to this app's own origin, which the Worker
 * forwards to the API over a service binding (D-05) — so there is no base URL,
 * no CORS, and the session cookie travels as a same-origin cookie.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The caller is not signed in, or the session was revoked. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

interface ApiErrorBody {
  error?: { code?: string; message?: string; details?: Record<string, unknown> };
}

async function toError(response: Response): Promise<ApiError> {
  let body: ApiErrorBody = {};
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    // A non-JSON error body (a proxy or edge failure) still has a status.
  }
  // A validation failure names the field, so the operator sees "points.0.active:
  // expected boolean" rather than a bare "not valid".
  const issues = body.error?.details?.issues as { path?: (string | number)[]; message?: string }[] | undefined;
  const detail = issues?.length ? ` — ${issues.slice(0, 3).map((i) => `${(i.path ?? []).join('.') || 'body'}: ${i.message ?? 'invalid'}`).join('; ')}` : '';
  return new ApiError(
    response.status,
    body.error?.code ?? 'UNKNOWN',
    `${body.error?.message ?? `Request failed (${response.status})`}${detail}`,
    body.error?.details,
  );
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      accept: 'application/json',
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      // Required on every non-GET request (docs/auth.md §7): cookies are
      // SameSite=Lax, and this header is what a cross-site form cannot send.
      ...(init.method && init.method !== 'GET' ? { 'x-bre-csrf': '1' } : {}),
      ...(init.headers as Record<string, string> | undefined),
    },
  });

  if (!response.ok) throw await toError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  del: <T>(path: string, body?: unknown) => request<T>(path, { method: 'DELETE', ...(body === undefined ? {} : { body: JSON.stringify(body) }) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** A raw body: the file is the request, which is how images are uploaded (D-87). */
  postRaw: <T>(path: string, body: BodyInit, contentType: string) =>
    request<T>(path, { method: 'POST', body, headers: { 'content-type': contentType } }),
};

// --- Shapes returned by the API (docs/auth.md §4.1) -------------------------

export interface SessionResponse {
  staff: { id: string; display_name: string; status: string };
  session: {
    id: string;
    surface: string;
    auth_method: string;
    device_id: string | null;
    expires_at: string;
  };
  venue_id: string | null;
  permissions: string[];
  /** Where the customer's side lives, for previewing what a buyer sees (D-300). */
  storefront_url: string;
}

export interface StaffRow {
  id: string;
  /** The address they sign in with, by Google or an emailed link (D-370). */
  login_email: string | null;
  google_linked: boolean;
  display_name: string;
  email_snapshot: string | null;
  status: string;
  has_pin: boolean;
  roles: string[];
  created_at: string;
}

export interface PendingLoginRow {
  email: string;
  name_snapshot: string | null;
  surface: string;
  auth_method: string;
  first_seen_at: string;
  last_seen_at: string;
  attempt_count: number;
}

export interface DeviceRow {
  id: string;
  device_type: string;
  status: string;
  name: string;
  venue_id: string | null;
  register_id: string | null;
  token_prefix: string | null;
  last_seen_at: string | null;
  paired_at: string | null;
  created_at: string;
}

export interface AlertRow {
  id: string;
  severity: string;
  code: string;
  message: string;
  subject_type: string | null;
  subject_id: string | null;
  created_at: string;
  acknowledged_at: string | null;
}

export interface SystemStatus {
  outbox: { undispatched: number; retrying: number; oldest_undispatched_at: string | null };
  alerts: { open: number; open_by_severity: Record<string, number>; recent: AlertRow[] };
  cron: { last_run_at: string | null; stale: boolean };
}

export interface VenueRow {
  id: string;
  slug: string;
  name: string;
  timezone: string;
  /** The address the API has always stored and Admin never asked for (D-150). */
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  region: string | null;
  postal_code: string | null;
  phone: string | null;
  public_notes: string | null;
  active: number;
  created_at: string;
}

export interface ManifestRow {
  id: string;
  venue_id: string;
  lineage_id: string;
  version: number;
  based_on_manifest_id: string | null;
  code: string;
  name: string;
  status: string;
  published_at: string | null;
  created_at: string;
}

export interface SectionRow {
  id: string;
  level_id: string | null;
  code: string;
  name: string;
  section_type: string;
  sort_order: number;
  geometry_json: string | null;
  label_x: number | null;
  label_y: number | null;
}

export interface LevelRow {
  id: string;
  code: string;
  name: string;
  sort_order: number;
}

export interface SeatRow {
  id: string;
  section_id: string;
  row_id: string | null;
  table_id: string | null;
  seat_number: string;
  sort_order: number;
  x: number;
  y: number;
  rotation: number;
  accessible: number;
  companion: number;
  companion_for_seat_id: string | null;
  restricted_view: number;
  /** Required, deliberately: optional is what let it be dropped from a SELECT unnoticed (D-292). */
  aisle: number;
  obstructed_view: number;
  production_kill_capable: number;
  active: number;
  public_note: string | null;
}

export interface TableRow {
  id: string;
  section_id: string;
  label: string;
  sell_mode: string;
  shape: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
}

/** What PATCH /seats accepts for a selection. */
export interface SeatUpdate {
  /** Rename one seat. One at a time; a series is what renumber is for (D-287). */
  seat_number?: string;
  accessible?: boolean;
  companion?: boolean;
  companion_for_seat_id?: string | null;
  restricted_view?: boolean;
  aisle?: boolean;
  obstructed_view?: boolean;
  production_kill_capable?: boolean;
  public_note?: string | null;
  dx?: number;
  dy?: number;
  rotation?: number;
}

export interface GaAreaRow {
  id: string;
  code: string;
  name: string;
  area_kind: string;
  capacity: number;
  accessible_capacity: number;
  active: number;
}

export interface ManifestEdit {
  id: string;
  operation: string;
  summary: string;
  created_at: string;
  undone_at: string | null;
}

export interface Canvas {
  canvas_x: number;
  canvas_y: number;
  canvas_width: number;
  canvas_height: number;
}

export interface ManifestLayout {
  manifest: ManifestRow & { canvas_width: number; canvas_height: number; background_url: string | null };
  levels: LevelRow[];
  sections: SectionRow[];
  rows: { id: string; section_id: string; label: string; sort_order: number; geometry_json: string | null }[];
  seats: SeatRow[];
  tables: TableRow[];
  ga_areas: (GaAreaRow & { section_id: string | null; geometry_json: string | null })[];
  capacity: { seats: number; ga_capacity: number };
}

export interface RowPatternInput {
  label: string;
  seat_count: number;
  first_seat_number?: number;
  numbering?: 'SEQUENTIAL' | 'ODD' | 'EVEN';
  reverse?: boolean;
  x: number;
  y: number;
  seat_spacing?: number;
  rotation?: number;
  curve_radius?: number;
}

export interface HealthResponse {
  service: string;
  status: string;
  app_environment: string;
}

/** A raw (non-JSON) upload; the content type describes the body. */
export async function putRaw<T>(path: string, body: BodyInit, contentType: string, extraHeaders: Record<string, string> = {}): Promise<T> {
  const response = await fetch(path, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { accept: 'application/json', 'content-type': contentType, 'x-bre-csrf': '1', ...extraHeaders },
    body,
  });
  if (!response.ok) throw await toError(response);
  return (await response.json()) as T;
}

export const endpoints = {
  session: () => api.get<SessionResponse>('/api/auth/session'),
  logout: () => api.post('/api/auth/logout'),
  staff: () => api.get<{ staff: StaffRow[] }>('/api/admin/staff'),
  pendingLogins: () => api.get<{ pending: PendingLoginRow[] }>('/api/admin/staff/pending'),
  promote: (email: string, roles: string[]) =>
    api.post<{ staff: StaffRow }>(`/api/admin/staff/pending/${encodeURIComponent(email)}/promote`, { roles }),
  dismissPending: (email: string) => api.del<{ dismissed: boolean }>(`/api/admin/staff/pending/${encodeURIComponent(email)}`),
  createStaff: (input: { display_name: string; login_email: string | null; roles: string[] }) => api.post<{ staff: StaffRow }>('/api/admin/staff', input),
  updateStaff: (id: string, input: { display_name?: string; login_email?: string | null }) => api.patch<{ staff: StaffRow }>(`/api/admin/staff/${id}`, input),
  setRoles: (id: string, roles: string[]) =>
    api.post<{ roles: string[] }>(`/api/admin/staff/${id}/roles`, { roles }),
  setPin: (id: string, pin: string) => api.post(`/api/admin/staff/${id}/pin`, { pin }),
  setStatus: (id: string, status: string, reason: string) =>
    api.post<{ staff: StaffRow }>(`/api/admin/staff/${id}/status`, { status, reason }),
  system: () => api.get<SystemStatus>('/api/admin/system'),
  acknowledgeAlert: (id: string) => api.post<{ acknowledged_at: string }>(`/api/admin/system/alerts/${id}/acknowledge`),
  acknowledgeAllAlerts: () => api.post<{ acknowledged: number }>('/api/admin/system/alerts/acknowledge-all'),
  venues: () => api.get<{ venues: VenueRow[] }>('/api/admin/venues'),
  venue: (id: string) =>
    api.get<{ venue: VenueRow; manifests: ManifestRow[]; accessibility: { features: AccessibilityFeature[]; catalog: AccessibilityFeatureRow[] } }>(`/api/admin/venues/${id}`),
  setVenueAccessibility: (id: string, input: { notes: string | null; features: { code: string; note: string | null }[] }) =>
    api.put<{ accessibility: { features: AccessibilityFeature[] } }>(`/api/admin/venues/${id}/accessibility`, input),
  createVenue: (input: Record<string, unknown>) => api.post<{ venue: VenueRow }>('/api/admin/venues', input),
  updateVenue: (id: string, input: Record<string, unknown>) =>
    api.patch<{ venue: VenueRow }>(`/api/admin/venues/${id}`, input),

  manifestLayout: (id: string) => api.get<ManifestLayout>(`/api/admin/manifests/${id}/layout`),
  /** Undo for the map editor (D-271). */
  manifestEdits: (id: string) => api.get<{ edits: ManifestEdit[] }>(`/api/admin/manifests/${id}/edits`),
  /** What is wrong with this room (D-274). */
  lintManifest: (id: string) =>
    api.get<{ problems: { severity: 'error' | 'warning'; code: string; message: string; seat_ids: string[] }[]; checked: { seats: number; rows: number; tables: number } }>(`/api/admin/manifests/${id}/lint`),
  undoManifestEdit: (id: string) => api.post<{ undone: ManifestEdit }>(`/api/admin/manifests/${id}/edits/undo`, {}),
  /** Bulk map operations (D-273). */
  arrangeSeats: (id: string, input: { seat_ids: string[]; operation: 'ALIGN' | 'DISTRIBUTE' | 'ROTATE' | 'FLIP'; align?: string; distribute?: string; flip?: string; degrees?: number }) =>
    api.post<{ moved: number }>(`/api/admin/manifests/${id}/seats/arrange`, input),
  renumberSeats: (id: string, input: { seat_ids: string[]; first: number; numbering: 'SEQUENTIAL' | 'ODD' | 'EVEN'; reverse: boolean; skip?: number[]; order?: 'SORT_ORDER' | 'POSITION' }) =>
    api.post<{ renumbered: number }>(`/api/admin/manifests/${id}/seats/renumber`, input),
  /** Which part of the drawing to show (D-267). */
  manifestCanvasFit: (id: string) =>
    api.get<{ fit: Canvas; current: Canvas }>(`/api/admin/manifests/${id}/canvas/fit`),
  setManifestCanvas: (id: string, input: Canvas) =>
    api.patch<{ manifest: ManifestRow }>(`/api/admin/manifests/${id}/canvas`, input),
  createManifest: (input: Record<string, unknown>) =>
    api.post<{ manifest: ManifestRow }>('/api/admin/manifests', input),
  publishManifest: (id: string) =>
    api.post<{ manifest: ManifestRow; capacity: { seats: number; ga_capacity: number } }>(
      `/api/admin/manifests/${id}/publish`,
    ),
  archiveManifest: (id: string, reason: string) =>
    api.post<{ manifest: ManifestRow }>(`/api/admin/manifests/${id}/archive`, { reason }),
  newManifestVersion: (id: string) =>
    api.post<{ manifest: ManifestRow; copied: Record<string, number> }>(`/api/admin/manifests/${id}/new-version`),

  createSection: (manifestId: string, input: Record<string, unknown>) =>
    api.post<{ section: SectionRow }>(`/api/admin/manifests/${manifestId}/sections`, input),
  /** Copy a section with its rows, tables and seats (D-298). */
  copySection: (manifestId: string, sectionId: string, input: { code: string; name: string; dx: number; dy: number }) =>
    api.post<{ section_id: string; seats: number; rows: number; tables: number }>(`/api/admin/manifests/${manifestId}/sections/${sectionId}/copy`, input),
  deleteSection: (manifestId: string, sectionId: string) =>
    api.del(`/api/admin/manifests/${manifestId}/sections/${sectionId}`),
  generateRows: (manifestId: string, sectionId: string, rows: RowPatternInput[]) =>
    api.post<{ rows_created: number; seats_created: number }>(
      `/api/admin/manifests/${manifestId}/sections/${sectionId}/generate-rows`,
      { rows },
    ),
  createGaArea: (manifestId: string, input: Record<string, unknown>) =>
    api.post<{ ga_area: GaAreaRow }>(`/api/admin/manifests/${manifestId}/ga-areas`, input),
  deleteGaArea: (manifestId: string, areaId: string) =>
    api.del(`/api/admin/manifests/${manifestId}/ga-areas/${areaId}`),

  importLayout: (manifestId: string, layout: unknown, replace: boolean) =>
    api.post<{ imported: Record<string, number>; replaced: boolean }>(`/api/admin/manifests/${manifestId}/import`, {
      ...(layout as Record<string, unknown>),
      replace,
    }),
  setBackground: (manifestId: string, body: BodyInit, contentType: string, filename: string) =>
    putRaw<{ background_file_id: string; background_url: string }>(
      `/api/admin/manifests/${manifestId}/background`,
      body,
      contentType,
      { 'x-original-filename': filename },
    ),
  clearBackground: (manifestId: string) => api.del<{ background_file_id: null; background_url: null }>(`/api/admin/manifests/${manifestId}/background`),
  createLevel: (manifestId: string, input: { code: string; name: string; sort_order?: number }) =>
    api.post<{ level: LevelRow }>(`/api/admin/manifests/${manifestId}/levels`, input),
  updateLevel: (manifestId: string, levelId: string, input: { code?: string; name?: string; sort_order?: number }) =>
    api.patch<{ level: { id: string; code: string; name: string; sort_order: number } }>(`/api/admin/manifests/${manifestId}/levels/${levelId}`, input),
  deleteLevel: (manifestId: string, levelId: string) => api.del(`/api/admin/manifests/${manifestId}/levels/${levelId}`),
  updateSection: (manifestId: string, sectionId: string, input: Record<string, unknown>) =>
    api.patch<{ section: SectionRow }>(`/api/admin/manifests/${manifestId}/sections/${sectionId}`, input),
  deleteRow: (manifestId: string, rowId: string) => api.del(`/api/admin/manifests/${manifestId}/rows/${rowId}`),
  createTable: (manifestId: string, sectionId: string, input: Record<string, unknown>) =>
    api.post<{ table: TableRow; seats_created: number }>(`/api/admin/manifests/${manifestId}/sections/${sectionId}/tables`, input),
  deleteTable: (manifestId: string, tableId: string) => api.del(`/api/admin/manifests/${manifestId}/tables/${tableId}`),
  /** Moves, turns or renames a table; its seats travel with it (D-72). */
  updateTable: (manifestId: string, tableId: string, set: { dx?: number; dy?: number; rotation?: number; label?: string }) =>
    api.patch<{ table: { id: string; label: string; x: number; y: number; rotation: number } }>(`/api/admin/manifests/${manifestId}/tables/${tableId}`, set),
  addSeat: (manifestId: string, input: Record<string, unknown>) =>
    api.post<{ seat: SeatRow }>(`/api/admin/manifests/${manifestId}/seats`, input),
  /** Rename a row, or reorder it within its section (D-290). */
  updateRow: (manifestId: string, rowId: string, input: { label?: string; sort_order?: number }) =>
    api.patch<{ row: { id: string; label: string; sort_order: number } }>(`/api/admin/manifests/${manifestId}/rows/${rowId}`, input),
  extendRow: (manifestId: string, rowId: string, input: { count: number; end: 'END' | 'START'; seat_numbers?: string[] }) =>
    api.post<{ seats_created: number; seat_numbers: string[] }>(`/api/admin/manifests/${manifestId}/rows/${rowId}/extend`, input),
  updateGaArea: (manifestId: string, areaId: string, input: Record<string, unknown>) =>
    api.patch<{ ga_area: GaAreaRow }>(`/api/admin/manifests/${manifestId}/ga-areas/${areaId}`, input),
  updateSeats: (manifestId: string, seatIds: string[], set: SeatUpdate) =>
    api.patch<{ seats: SeatRow[] }>(`/api/admin/manifests/${manifestId}/seats`, { seat_ids: seatIds, set }),
  deleteSeats: (manifestId: string, seatIds: string[]) =>
    request<{ deleted: number }>(`/api/admin/manifests/${manifestId}/seats`, {
      method: 'DELETE',
      body: JSON.stringify({ seat_ids: seatIds }),
    }),

  seedEvents: () => api.post<{ seeded: { slug: string; title: string; units: number }[]; skipped: string[] }>('/api/admin/dev-tools/seed-events'),
  /** Haunted Auditorium 2026: six nights of entry times, plus walk-up tickets (D-86). */
  seedHauntedAuditorium: () =>
    api.post<{
      seeded: { event_id: string; slug: string; performances: { id: string; local_date: string; slots: number; admissions: number; walkup_units: number }[]; slots_total: number; admissions_total: number } | null;
      skipped: string | null;
      summary?: { admissions_total: number } | null;
    }>('/api/admin/dev-tools/seed-haunted-auditorium'),
  health: () => api.get<HealthResponse>('/api/health'),
  seedVenues: () =>
    api.post<{ seeded: { slug: string; name: string; manifest_id: string; counts: Record<string, number> }[]; skipped: string[] }>(
      '/api/admin/dev-tools/seed-venues',
    ),

  devices: () => api.get<{ devices: DeviceRow[] }>('/api/admin/devices'),
  createDevice: (input: { device_type: string; name: string; venue_id?: string | null; register_id?: string | null }) =>
    api.post<{ device: DeviceRow }>('/api/admin/devices', input),
  pairingCode: (id: string) =>
    api.post<{ code: string; expires_at: string }>(`/api/admin/devices/${id}/pairing-code`),
  revokeDevice: (id: string, reason: string) =>
    api.post<{ device: DeviceRow }>(`/api/admin/devices/${id}/revoke`, { reason }),
};

// --- Phase 3: events, performances, pricing, fees ---------------------------

export interface EventRow {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  category_id: string | null;
  /** The marketing strand and the dated grouping this event is in (D-169). */
  series_id: string | null;
  season_id: string | null;
  primary_venue_id: string;
  accounting_mode: string;
  renter_organization_id: string | null;
  promoter_organization_id: string | null;
  status: string;
  visibility: string;
  featured: number;
  sort_weight: number;
  age_restriction: string | null;
  minimum_age: number;
  arrival_note: string | null;
  parking_note: string | null;
  post_event_note: string | null;
  survey_url: string | null;
  loyalty_earn?: number;
  loyalty_redeem?: number;
  ticket_limit_per_order: number | null;
  exchange_policy_id?: string | null;
  refund_policy_id?: string | null;
  ticket_limit_per_customer: number | null;
  ticket_limit_per_household: number | null;
  announce_at: string | null;
  artwork_file_id: string | null;
  hero_file_id: string | null;
  /** The team this game is against; null is "not a game" (D-368). */
  opponent_id?: string | null;
  /** Per game (D-373): vouchers are refused for it; season passes do not cover it. */
  voucher_excluded?: number;
  playoff_game?: number;
  /** Played only if the series needs it (D-374). */
  if_necessary?: number;
  /** Made a single-admission doubleheader (D-374). */
  doubleheader?: number;
  created_at: string;
  updated_at: string;
  venue_name?: string;
  performance_count?: number;
  first_starts_at?: string | null;
}

/** Delivery methods per event (D-58): eDelivery / will call / mail, each with a fee and a cutoff in days before the performance. */
export interface EventDeliveryMethod {
  event_id: string;
  delivery_method: 'MOBILE' | 'WILL_CALL' | 'MAIL';
  enabled: boolean;
  fee_cents: number;
  cutoff_days: number | null;
}

export interface DeliveryAddress {
  recipient_name: string;
  address_line1: string;
  address_line2: string | null;
  city: string;
  region: string;
  postal_code: string;
  country: string;
}

/** An order to fulfill by mail or will call, as Admin → Mail & will call lists it. */
export interface FulfillmentPerformance {
  id: string;
  event_title: string;
  starts_at: string;
  timezone: string;
  venue_id: string;
}

export interface FulfillmentOrder {
  id: string;
  order_number: string;
  status: string;
  delivery_method: string;
  customer_name: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  completed_at: string | null;
  fulfillment_printed_at: string | null;
  pickups: number;
  tickets: { id: string; status: string; credential_version: number; event_title: string | null; starts_at: string | null; timezone: string; seat_label: string; ticket_type: string | null; printed: boolean }[];
  address: DeliveryAddress | null;
}

export interface FulfillmentBatch {
  batch_id: string;
  orders: number;
  header_job_ids: string[];
  ticket_job_ids: string[];
}

export interface PerformanceRow {
  id: string;
  venue_id: string;
  manifest_id: string;
  code: string;
  name_override: string | null;
  starts_at: string;
  doors_at: string | null;
  ends_at: string | null;
  local_event_date: string;
  timezone: string;
  status: string;
  reentry_policy: string;
  inventory_initialized_at: string | null;
  public_notes: string | null;
  /** A rained-out game (D-374). */
  rained_out_at?: string | null;
  rain_out_reason?: string | null;
  channel_windows?: ChannelWindow[];
}

export type RainCheckKind = 'CREDIT' | 'COMP' | 'SEASON_PASS' | 'VOUCHER';

/** The rainout's dry run and progress report (D-374). */
export interface RainOutSummary {
  performance_id: string;
  event_title: string;
  status: string;
  rained_out_at: string | null;
  rain_out_reason: string | null;
  can_rain_out: boolean;
  policy: RainCheckPolicy;
  valid_through: string | null;
  outstanding: { orders: number; tickets: number; admitted: number; by_kind: Record<RainCheckKind, { tickets: number; cents: number }>; no_account_orders: number };
  issued: { tickets: number; customers: number; by_kind: Record<RainCheckKind, { tickets: number; cents: number }>; replacement_vouchers: number };
}

export interface RainOutPage {
  dry_run: false;
  newly_rained_out: boolean;
  processed_orders: { order_id: string; order_number: string; credit_cents: number; tickets: number; kinds: RainCheckKind[]; refund_number: string | null }[];
  failed_orders: { order_id: string; order_number: string; code: string; message: string }[];
  next_after_order_id: string | null;
  summary: RainOutSummary;
}

/** The rain-check policy (D-374). */
export interface RainCheckPolicy {
  validity: 'SAME_SEASON' | 'ANY_FUTURE' | 'DAYS';
  validity_days: number | null;
  include_fees: boolean;
  doubleheader_default: 'SINGLE_ADMISSION' | 'SEPARATE_ADMISSION';
  doubleheader_title: string;
  doubleheader_note: string;
}

export interface ChannelWindow {
  channel: string;
  opens_at: string | null;
  closes_at: string | null;
  enabled: number | boolean;
}

export interface TicketTypeRow {
  id: string;
  code: string;
  name: string;
  category: string;
  is_comp_type: number;
  requires_access_code: number;
  min_per_order: number;
  max_per_order: number | null;
  sort_order: number;
  active: number;
  channels?: string[];
  /** Sold against an entry time: the buyer picks when to arrive (D-86). */
  timed_entry?: number;
}

export interface PriceZoneRow {
  id: string;
  code: string;
  name: string;
  color_hex: string | null;
  sort_order: number;
  /** 0 when retired: off the website and the pickers, kept for history (D-156). */
  active?: number;
  assignments?: { id: string; performance_id: string | null; section_id: string | null; row_id: string | null; table_id: string | null; seat_id: string | null; ga_area_id: string | null }[];
}

export interface PricingTierRow {
  id: string;
  code: string;
  name: string;
  sort_order: number;
  starts_at: string | null;
  ends_at: string | null;
  quantity_limit: number | null;
}

export interface PriceTableVersionRow {
  id: string;
  version: number;
  status: string;
  effective_from: string | null;
  activated_at: string | null;
  reason: string | null;
  price_points?: number;
}

export interface PricePointRow {
  id?: string;
  performance_id: string | null;
  pricing_tier_id: string | null;
  price_zone_id: string;
  ticket_type_id: string;
  channel: string | null;
  face_value_cents: number;
  list_price_cents: number;
  active: number | boolean;
}

export interface RevenueRuleRow {
  id?: string;
  revenue_component: string;
  line_type: string;
  revenue_owner_type: string;
  revenue_owner_organization_id: string | null;
  organization_name?: string | null;
  basis_points: number;
  accounting_code: string;
  settlement_behavior: string;
  rounding_priority: number;
}

export interface RevenueRuleSetRow {
  id: string;
  version: number;
  accounting_mode: string;
  status: string;
  effective_from: string | null;
  rules: RevenueRuleRow[];
}

export interface FeeRuleRow {
  id: string;
  code: string;
  fee_type: string;
  venue_id: string | null;
  active: number;
  active_version_id: string | null;
  active_version: number | null;
  name: string | null;
  calculation_method: string | null;
  application: string | null;
  amount_cents: number | null;
  basis_points: number | null;
  included_in_price: number | null;
  refundable: number | null;
  taxable: number | null;
  revenue_owner_type: string | null;
  drafts: number;
}

export interface FeeVersionInput {
  name: string;
  calculation_method: string;
  application: string;
  price_basis: string;
  amount_cents?: number | null;
  basis_points?: number | null;
  min_cents?: number | null;
  max_cents?: number | null;
  included_in_price?: boolean;
  revenue_owner_type?: string;
  revenue_owner_organization_id?: string | null;
  refundable: boolean;
  taxable?: boolean;
  accounting_code: string;
  settlement_behavior?: string;
  effective_from?: string;
  channels?: string[];
  price_bands?: { min_price_cents: number; max_price_cents: number | null; amount_cents: number }[];
}

export interface TaxRuleRow {
  id: string;
  code: string;
  jurisdiction: string;
  active: number;
  active_version_id: string | null;
  active_version: number | null;
  name: string | null;
  rate_ppm: number | null;
  drafts: number;
}

export interface OrganizationRow {
  id: string;
  name: string;
  legal_name: string | null;
  active: number;
}

export interface ExchangePolicyRow {
  id: string;
  code: string;
  name: string;
  public_text: string;
  customer_self_service: number;
  self_service_cutoff_minutes_before_start: number;
  fee_cents: number;
  downgrade_disposition: 'REFUND' | 'FORFEIT';
  active: number;
}

/** A category in the catalog (D-155). Events belong to any number of them. */
export interface EventCategoryRow {
  id: string;
  slug: string;
  name: string;
  sort_order: number;
  active: number;
}

export interface EventDetail {
  event: EventRow;
  /** Every category the event is in; `event.category_id` is the primary (D-155). */
  categories?: EventCategoryRow[];
  exchange_policy: ExchangePolicyRow | null;
  /** The flexible-ticket policy this event points at, if any (D-190). */
  refund_policy: RefundPolicyRow | null;
  venue: { id: string; slug: string; name: string; timezone: string };
  performances: PerformanceRow[];
  ticket_types: TicketTypeRow[];
  price_zones: PriceZoneRow[];
  pricing_tiers: PricingTierRow[];
  active_price_table: (PriceTableVersionRow & { price_points: number }) | null;
  active_revenue_rule_set: { id: string; version: number; accounting_mode: string } | null;
  fee_rules: { id: string; code: string; fee_type: string; active: number; name: string | null; version_status: string | null }[];
  tax_rules: { id: string; code: string; jurisdiction: string; active: number; name: string | null; rate_ppm: number | null }[];
}

export interface QuoteLine {
  item_key: string | null;
  unit: number | null;
  line_type: string;
  amount_cents: number;
  revenue_owner_type: string;
  revenue_owner_organization_id: string | null;
  accounting_code: string;
  settlement_behavior: string;
  refundable: boolean;
  included_in_face_value: boolean;
  name: string;
}

export interface Quote {
  quote: {
    items: { key: string; quantity: number; unit: Record<string, number>; pricing_tier_id: string | null }[];
    lines: QuoteLine[];
    totals: Record<string, number>;
  };
  snapshot: Record<string, unknown>;
}

/** The accessibility catalog and what a venue or event has chosen from it (D-80). */
/** A flexible-ticket policy: the window, the rate, and what the customer is told (D-190). */
export interface RefundPolicyRow {
  id: string;
  code: string;
  name: string;
  public_text: string;
  customer_self_service: number;
  self_service_cutoff_minutes_before_start: number | null;
  flexible_basis_points: number;
  active: number;
}

export interface AccessibilityFeatureRow {
  code: string;
  name: string;
  description: string;
  sort_order: number;
}
export interface AccessibilityFeature extends AccessibilityFeatureRow {
  note: string | null;
  /** Event only: the room offers this and this show does not (D-186). */
  suppressed?: boolean;
}

/** Surveys asked through this platform (D-168). */
export interface SurveyRow {
  id: string;
  event_id: string;
  name: string;
  status: 'DRAFT' | 'OPEN' | 'CLOSED';
  intro_md: string | null;
  thanks_md: string | null;
  closes_after_days: number | null;
  created_at?: string;
  questions?: number;
  invited?: number;
  answered?: number;
}

export interface SurveyQuestion {
  id: string;
  kind: 'RATING' | 'SINGLE_CHOICE' | 'MULTI_CHOICE' | 'TEXT';
  prompt: string;
  help_text: string | null;
  required: number;
  sort_order: number;
  options: { id: string; label: string }[];
}

export interface SurveyResults {
  survey: SurveyRow;
  invited: number;
  answered: number;
  by_performance: { performance_id: string; starts_at: string; timezone: string; responses: number; average_rating: number | null }[];
  questions: {
    id: string;
    kind: string;
    prompt: string;
    answered: number;
    average_rating: number | null;
    counts: { label: string; count: number }[];
    text_answers: { text: string; submitted_at: string; performance_id: string }[];
  }[];
}

export const surveyEndpoints = {
  forEvent: (eventId: string) => api.get<{ surveys: SurveyRow[] }>(`/api/admin/surveys/events/${eventId}`),
  create: (eventId: string, input: { name: string; intro_md?: string | null; thanks_md?: string | null; closes_after_days?: number | null }) =>
    api.post<{ survey: SurveyRow }>(`/api/admin/surveys/events/${eventId}`, input),
  detail: (id: string) => api.get<{ survey: SurveyRow; questions: SurveyQuestion[] }>(`/api/admin/surveys/${id}`),
  update: (id: string, input: Record<string, unknown>) => api.patch<{ survey: SurveyRow }>(`/api/admin/surveys/${id}`, input),
  setStatus: (id: string, status: 'DRAFT' | 'OPEN' | 'CLOSED') => api.post<{ survey: SurveyRow }>(`/api/admin/surveys/${id}/status`, { status }),
  addQuestion: (id: string, input: { kind: string; prompt: string; help_text?: string | null; required: boolean; sort_order: number; options: string[] }) =>
    api.post<{ questions: SurveyQuestion[] }>(`/api/admin/surveys/${id}/questions`, input),
  removeQuestion: (id: string, questionId: string) => api.del<{ questions: SurveyQuestion[] }>(`/api/admin/surveys/${id}/questions/${questionId}`),
  results: (id: string) => api.get<SurveyResults>(`/api/admin/surveys/${id}/results`),
};

/** A note on an order (D-169). Append-only: there is no edit and no delete. */
export interface OrderNote {
  id: string;
  body: string;
  visibility: 'INTERNAL' | 'CUSTOMER';
  author_name: string | null;
  created_at: string;
}

/** A series or a season (D-169). Seasons carry dates; series carry an order. */
export interface CollectionRow {
  id: string;
  code: string;
  slug: string | null;
  name: string;
  description: string | null;
  active: number;
  sort_order?: number;
  starts_on?: string;
  ends_on?: string;
  events?: number;
  /** Series only (D-375): the team a run of games is against, and its season. */
  opponent_id?: string | null;
  season_id?: string | null;
  opponent_name?: string | null;
  season_name?: string | null;
  /** Series only: games not archived, and the first and last of their dates (derived, never stored). */
  games?: number;
  first_date?: string | null;
  last_date?: string | null;
}

/** A house account and what it owes (D-173). The balance is derived, never stored. */
export interface HouseAccountRow {
  organization_id: string;
  name: string;
  enabled: boolean;
  limit_cents: number | null;
  terms_days: number;
  statement_email: string | null;
  charged_cents: number;
  settled_cents: number;
  balance_cents: number;
  available_cents: number | null;
  oldest_unsettled_on: string | null;
}

export interface HouseAccountStatement {
  account: HouseAccountRow;
  lines: { kind: 'CHARGE' | 'SETTLEMENT'; on: string; order_number: string | null; description: string; amount_cents: number }[];
  aging: { bucket: string; amount_cents: number }[];
}

export const houseAccountEndpoints = {
  list: () => api.get<{ accounts: HouseAccountRow[] }>('/api/admin/organizations/house-accounts'),
  set: (organizationId: string, input: { enabled: boolean; limit_cents: number | null; terms_days: number; statement_email: string | null }) =>
    api.put<{ account: HouseAccountRow }>(`/api/admin/organizations/${organizationId}/house-account`, input),
  statement: (organizationId: string) => api.get<HouseAccountStatement>(`/api/admin/organizations/${organizationId}/house-account/statement`),
  settle: (organizationId: string, input: { amount_cents: number; method: string; reference: string | null; received_on: string }) =>
    api.post<{ account: HouseAccountRow }>(`/api/admin/organizations/${organizationId}/house-account/settlements`, input),
};

/** An organization the venue sells to or rents to, as a record (D-244). */
export interface OrganizationRow {
  id: string;
  name: string;
  legal_name: string | null;
  active: number;
  primary_email: string | null;
  phone: string | null;
  address_line1: string | null;
  city: string | null;
  region: string | null;
  postal_code: string | null;
  notes: string | null;
  created_at: string;
  types: string[];
}

/** A person at an organization (D-254), stored in the table 0013 built for renters. */
export interface OrganizationContact {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  title: string | null;
  status: string;
  bre_auth_sub: string | null;
}

export interface OrganizationDetail {
  organization: OrganizationRow & { house_account_enabled: number; house_account_limit_cents: number | null; house_account_terms_days: number | null };
  contacts: OrganizationContact[];
  group_holds: { id: string; seat_count: number; status: string; created_at: string; contact_name: string; contact_email: string; event_title: string; starts_at: string; order_number: string; cached_total_due_cents: number }[];
  sponsorships: { id: string; name: string }[];
  /** The QuickBooks customer its group receivables post under, once one exists (D-352). */
  qbo_customer?: { id: string; name: string | null } | null;
  /** Group sales (D-376): its outings, its leads, and what its group orders came to. */
  group_bookings?: { id: string; lead_id: string | null; status: string; product_name: string; option_names: string | null; event_title: string; starts_at: string; ticket_count: number; order_id: string | null; order_number: string | null; total_cents: number | null; paid_cents: number | null }[];
  group_leads?: { id: string; reference: string; status: string; status_name: string; contact_name: string; created_at: string; interest_name: string | null; booked_cents: number }[];
  group_spend?: { orders: number; total_cents: number; paid_cents: number };
}

/** Every hold across events (D-258), for the Holds screen. */
export interface HoldRow {
  id: string;
  name: string;
  notes: string | null;
  /** The window, open on this performance, where the hold can be sold out of (D-330). */
  sell_url?: string;
  auto_release_at: string | null;
  closed_at: string | null;
  created_at: string;
  hold_class: string;
  hold_class_name: string;
  inventory_state: string;
  owner_organization_id: string | null;
  owner_name: string | null;
  event_id: string;
  event_title: string;
  performance_id: string;
  starts_at: string;
  timezone: string;
  venue_name: string;
  units: number;
}

export const holdEndpoints = {
  list: (params: { event_id?: string; performance_id?: string; include_closed?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (params.event_id) q.set('event_id', params.event_id);
    if (params.performance_id) q.set('performance_id', params.performance_id);
    if (params.include_closed) q.set('include_closed', '1');
    const suffix = q.toString();
    return api.get<{ holds: HoldRow[] }>(`/api/admin/holds${suffix ? `?${suffix}` : ''}`);
  },
  release: (eventId: string, performanceId: string, holdId: string, unitIds?: string[]) =>
    api.post<{ hold_id: string; released: number; closed: boolean }>(
      `/api/admin/events/${eventId}/performances/${performanceId}/inventory/holds/${holdId}/release`,
      unitIds ? { unit_ids: unitIds } : {},
    ),
};

export const organizationEndpoints = {
  list: () => api.get<{ organizations: OrganizationRow[] }>('/api/admin/organizations'),
  types: () => api.get<{ types: { code: string; name: string }[] }>('/api/admin/organizations/types'),
  get: (id: string) => api.get<OrganizationDetail>(`/api/admin/organizations/${id}`),
  create: (input: Record<string, unknown>) => api.post<{ organization: OrganizationRow }>('/api/admin/organizations', input),
  update: (id: string, input: Record<string, unknown>) => api.patch<{ organization: OrganizationRow }>(`/api/admin/organizations/${id}`, input),
  addContact: (id: string, input: { name: string; email: string; phone?: string | null; title?: string | null }) =>
    api.post<{ contact: OrganizationContact }>(`/api/admin/organizations/${id}/contacts`, input),
  removeContact: (id: string, contactId: string) => api.del<{ removed: boolean }>(`/api/admin/organizations/${id}/contacts/${contactId}`),
};

export interface EventDeletionPreview {
  event: { id: string; title: string; slug: string; status: string };
  /** Why it cannot be deleted, in the words somebody would use. Empty means it can. */
  refusals: { noun: string; rows: number; reason: string }[];
  removals: { table: string; rows: number }[];
  total_rows: number;
}

export const eventEndpoints = {
  list: (status?: string) => api.get<{ events: EventRow[] }>(`/api/admin/events${status ? `?status=${status}` : ''}`),
  categories: (all = false) => api.get<{ categories: EventCategoryRow[] }>(`/api/admin/events/categories${all ? '?all=1' : ''}`),
  series: (all = false, seasonId?: string | null) => {
    const query = new URLSearchParams();
    if (all) query.set('all', '1');
    if (seasonId) query.set('season_id', seasonId);
    return api.get<{ series: CollectionRow[] }>(`/api/admin/events/series${query.size ? `?${query}` : ''}`);
  },
  createSeries: (input: Record<string, unknown>) => api.post<{ series: CollectionRow }>('/api/admin/events/series', input),
  updateSeries: (id: string, input: Record<string, unknown>) => api.patch<{ series: CollectionRow }>(`/api/admin/events/series/${id}`, input),
  seasons: (all = false) => api.get<{ seasons: CollectionRow[] }>(`/api/admin/events/seasons${all ? '?all=1' : ''}`),
  createSeason: (input: Record<string, unknown>) => api.post<{ season: CollectionRow }>('/api/admin/events/seasons', input),
  updateSeason: (id: string, input: Record<string, unknown>) => api.patch<{ season: CollectionRow }>(`/api/admin/events/seasons/${id}`, input),
  createCategory: (input: { name: string; slug?: string; sort_order?: number }) => api.post<{ category: EventCategoryRow }>('/api/admin/events/categories', input),
  updateCategory: (id: string, input: Record<string, unknown>) => api.patch<{ category: EventCategoryRow }>(`/api/admin/events/categories/${id}`, input),
  detail: (id: string) => api.get<EventDetail>(`/api/admin/events/${id}`),
  create: (input: Record<string, unknown>) => api.post<{ event: EventRow }>('/api/admin/events', input),
  update: (id: string, input: Record<string, unknown>) => api.patch<{ event: EventRow }>(`/api/admin/events/${id}`, input),
  setStatus: (id: string, status: string, reason?: string) => api.post<{ event: EventRow }>(`/api/admin/events/${id}/status`, { status, reason }),
  /** What deleting this event would take, and what stops it (D-308). */
  deletion: (id: string) => api.get<EventDeletionPreview>(`/api/admin/events/${id}/deletion`),
  remove: (id: string, confirmTitle: string) => api.delete<{ deleted: { table: string; rows: number }[] }>(`/api/admin/events/${id}?confirm=${encodeURIComponent(confirmTitle)}`),
  /** Copy an event's configuration onto a new draft (D-164). */
  duplicate: (id: string, input: { slug: string; title: string; performance_dates?: string[] }) =>
    api.post<{ event: EventRow; copied: Record<string, number>; performances: number; notes: string[] }>(`/api/admin/events/${id}/duplicate`, input),
  refundPolicies: () => api.get<{ policies: RefundPolicyRow[] }>('/api/admin/events/refund-policies/all'),
  accessibility: (id: string) =>
    api.get<{ features: AccessibilityFeature[]; catalog: AccessibilityFeatureRow[]; inherited: AccessibilityFeature[]; notes: string | null; good_to_know_md: string | null }>(
      `/api/admin/events/${id}/accessibility`,
    ),
  setAccessibility: (id: string, input: { notes: string | null; good_to_know_md: string | null; features: { code: string; note: string | null; suppressed?: boolean }[] }) =>
    api.put<{ features: AccessibilityFeature[] }>(`/api/admin/events/${id}/accessibility`, input),
  embedOrigins: (eventId: string) => api.get<{ origins: { id: string; origin: string; created_at: string }[]; snippet_host: string }>(`/api/admin/events/${eventId}/embed-origins`),
  setEmbedOrigins: (eventId: string, origins: string[]) => api.put<{ origins: string[] }>(`/api/admin/events/${eventId}/embed-origins`, { origins }),
  deliveryMethods: (id: string) => api.get<{ methods: EventDeliveryMethod[] }>(`/api/admin/events/${id}/delivery-methods`),
  setDeliveryMethods: (id: string, methods: Omit<EventDeliveryMethod, 'event_id'>[]) => api.put<{ methods: EventDeliveryMethod[] }>(`/api/admin/events/${id}/delivery-methods`, { methods }),
  uploadImage: (id: string, kind: 'artwork' | 'hero', file: File) => putRaw<{ event: EventRow }>(`/api/admin/events/${id}/${kind}`, file, file.type),
  removeImage: (id: string, kind: 'artwork' | 'hero') => api.del<{ event: EventRow }>(`/api/admin/events/${id}/${kind}`),
  imageUrl: (id: string, kind: 'artwork' | 'hero') => `/api/admin/events/${id}/${kind}`,
  organizations: () => api.get<{ organizations: OrganizationRow[] }>('/api/admin/organizations'),
  createOrganization: (name: string) => api.post<{ organization: OrganizationRow }>('/api/admin/organizations', { name }),

  performances: (id: string) => api.get<{ performances: PerformanceRow[] }>(`/api/admin/events/${id}/performances`),
  createPerformance: (id: string, input: Record<string, unknown>) => api.post<{ performance: PerformanceRow }>(`/api/admin/events/${id}/performances`, input),
  updatePerformance: (id: string, performanceId: string, input: Record<string, unknown>) =>
    api.patch<{ performance: PerformanceRow; notified?: number }>(`/api/admin/events/${id}/performances/${performanceId}`, input),
  /** Raining a game out (D-374): GET is the dry run and progress; POST marks it and issues the next page of rain checks. */
  rainOutSummary: (eventId: string, performanceId: string) => api.get<RainOutSummary>(`/api/admin/events/${eventId}/performances/${performanceId}/rain-out`),
  rainOut: (eventId: string, performanceId: string, input: { reason: string; limit?: number; after_order_id?: string | null }) => api.post<RainOutPage>(`/api/admin/events/${eventId}/performances/${performanceId}/rain-out`, input),
  /** A single-admission doubleheader (D-374). */
  makeDoubleheader: (eventId: string, performanceId: string, input: { starts_at: string; title?: string | null; note?: string | null; notify_holders: boolean }) =>
    api.post<{ performance: PerformanceRow; title: string; note: string; notified: number }>(`/api/admin/events/${eventId}/performances/${performanceId}/doubleheader`, input),
  /** An "if necessary" game that was not needed: cancel and refund (D-374). */
  notNeeded: (eventId: string, performanceId: string) =>
    api.post<{ refunded_orders: { order_number: string; refund_number: string; amount_cents: number; tickets: number; stripe_ok: boolean; message: string | null }[]; failed_orders: { order_number: string; code: string; message: string }[]; summary: PerformanceCancellation }>(
      `/api/admin/events/${eventId}/performances/${performanceId}/not-needed`,
      {},
    ),
  /** What a canceled performance still owes its ticket holders, and the batch that refunds them (D-72). */
  cancellation: (eventId: string, performanceId: string) => api.get<PerformanceCancellation>(`/api/admin/events/${eventId}/performances/${performanceId}/cancellation`),
  refundCancelled: (eventId: string, performanceId: string, input: { limit?: number; reason_text?: string | null; refund_fees: boolean }) =>
    api.post<{ refunded_orders: { order_number: string; refund_number: string; amount_cents: number; tickets: number; stripe_ok: boolean; message: string | null }[]; failed_orders: { order_number: string; code: string; message: string }[]; summary: PerformanceCancellation }>(
      `/api/admin/events/${eventId}/performances/${performanceId}/refund-batch`,
      input,
    ),
  /** Postponing and rescheduling (D-174): their own actions, not a status change. */
  postponePerformance: (id: string, performanceId: string, reason: string) =>
    api.post<{ notified: number; postponed_from: string }>(`/api/admin/events/${id}/performances/${performanceId}/postpone`, { reason }),
  reschedulePerformance: (id: string, performanceId: string, input: { starts_at: string; on_sale: boolean }) =>
    api.post<{ notified: number; reissued: number }>(`/api/admin/events/${id}/performances/${performanceId}/reschedule`, input),
  setPerformanceStatus: (id: string, performanceId: string, status: string, reason?: string) =>
    api.post<{ performance: PerformanceRow }>(`/api/admin/events/${id}/performances/${performanceId}/status`, { status, reason }),
  setChannelWindows: (id: string, performanceId: string, windows: ChannelWindow[]) =>
    api.put<{ windows: ChannelWindow[] }>(`/api/admin/events/${id}/performances/${performanceId}/channel-windows`, { windows }),

  ticketTypes: (id: string) => api.get<{ ticket_types: TicketTypeRow[] }>(`/api/admin/events/${id}/ticket-types`),
  createTicketType: (id: string, input: Record<string, unknown>) => api.post<{ ticket_type: TicketTypeRow }>(`/api/admin/events/${id}/ticket-types`, input),
  updateTicketType: (id: string, typeId: string, input: Record<string, unknown>) =>
    api.patch<{ ticket_type: TicketTypeRow }>(`/api/admin/events/${id}/ticket-types/${typeId}`, input),
  priceZones: (id: string) => api.get<{ price_zones: PriceZoneRow[] }>(`/api/admin/events/${id}/price-zones`),
  createPriceZone: (id: string, input: Record<string, unknown>) => api.post<{ price_zone: PriceZoneRow }>(`/api/admin/events/${id}/price-zones`, input),
  updatePriceZone: (id: string, zoneId: string, input: Record<string, unknown>) => api.patch<{ price_zone: PriceZoneRow }>(`/api/admin/events/${id}/price-zones/${zoneId}`, input),
  deletePriceZone: (id: string, zoneId: string) => api.del(`/api/admin/events/${id}/price-zones/${zoneId}`),
  setZoneAssignments: (id: string, zoneId: string, targets: Record<string, string>[], performanceId?: string | null, mode: 'replace' | 'add' | 'remove' = 'replace') =>
    api.put<{ assignments: number }>(`/api/admin/events/${id}/price-zones/${zoneId}/assignments`, { targets, performance_id: performanceId ?? null, mode }),
  effectiveZones: (id: string, manifestId: string, performanceId?: string | null) =>
    api.get<{ seats: Record<string, string | null>; ga_areas: Record<string, string | null> }>(`/api/admin/events/${id}/price-zones/effective?manifest_id=${manifestId}${performanceId ? `&performance_id=${performanceId}` : ''}`),
  createTier: (id: string, input: Record<string, unknown>) => api.post<{ pricing_tier: PricingTierRow }>(`/api/admin/events/${id}/pricing-tiers`, input),
  updateTier: (id: string, tierId: string, input: Record<string, unknown>) => api.patch<{ pricing_tier: PricingTierRow }>(`/api/admin/events/${id}/pricing-tiers/${tierId}`, input),
  deleteTier: (id: string, tierId: string) => api.del(`/api/admin/events/${id}/pricing-tiers/${tierId}`),

  priceTables: (id: string) => api.get<{ versions: PriceTableVersionRow[] }>(`/api/admin/events/${id}/price-tables`),
  priceTable: (id: string, versionId: string) => api.get<{ version: PriceTableVersionRow; points: PricePointRow[] }>(`/api/admin/events/${id}/price-tables/${versionId}`),
  draftPriceTable: (id: string, reason?: string) => api.post<{ version: PriceTableVersionRow }>(`/api/admin/events/${id}/price-tables`, { reason }),
  setPricePoints: (id: string, versionId: string, points: PricePointRow[]) =>
    api.put<{ points: number }>(`/api/admin/events/${id}/price-tables/${versionId}/points`, { points }),
  activatePriceTable: (id: string, versionId: string) => api.post<{ version: PriceTableVersionRow }>(`/api/admin/events/${id}/price-tables/${versionId}/activate`),
  discardPriceTable: (id: string, versionId: string) => api.del(`/api/admin/events/${id}/price-tables/${versionId}`),

  revenueRuleSets: (id: string) => api.get<{ rule_sets: RevenueRuleSetRow[] }>(`/api/admin/events/${id}/revenue-rule-sets`),
  draftRevenueRuleSet: (id: string, accountingMode?: string) =>
    api.post<{ rule_set_id: string }>(`/api/admin/events/${id}/revenue-rule-sets`, accountingMode ? { accounting_mode: accountingMode } : {}),
  setRevenueRules: (id: string, setId: string, rules: RevenueRuleRow[]) =>
    api.put<{ rules: number }>(`/api/admin/events/${id}/revenue-rule-sets/${setId}/rules`, { rules }),
  activateRevenueRuleSet: (id: string, setId: string) => api.post(`/api/admin/events/${id}/revenue-rule-sets/${setId}/activate`),

  attachFeeRule: (id: string, feeRuleId: string) => api.post(`/api/admin/events/${id}/fee-rules`, { fee_rule_id: feeRuleId }),
  detachFeeRule: (id: string, feeRuleId: string) => api.del(`/api/admin/events/${id}/fee-rules/${feeRuleId}`),
  attachTaxRule: (id: string, taxRuleId: string) => api.post(`/api/admin/events/${id}/tax-rules`, { tax_rule_id: taxRuleId }),
  detachTaxRule: (id: string, taxRuleId: string) => api.del(`/api/admin/events/${id}/tax-rules/${taxRuleId}`),
  quote: (id: string, input: Record<string, unknown>) => api.post<Quote>(`/api/admin/events/${id}/quote`, input),

  feeRules: () => api.get<{ fee_rules: FeeRuleRow[] }>('/api/admin/fee-rules'),
  createFeeRule: (input: { code: string; fee_type: string; venue_id?: string | null; version: FeeVersionInput }) =>
    api.post<{ fee_rule_id: string }>('/api/admin/fee-rules', input),
  draftFeeVersion: (ruleId: string, version: FeeVersionInput) => api.post<{ version: { id: string } }>(`/api/admin/fee-rules/${ruleId}/versions`, version),
  activateFeeVersion: (ruleId: string, versionId: string) => api.post(`/api/admin/fee-rules/${ruleId}/versions/${versionId}/activate`),
  setFeeRuleActive: (ruleId: string, active: boolean) => api.patch(`/api/admin/fee-rules/${ruleId}`, { active }),
  feeRule: (ruleId: string) => api.get<{ fee_rule: FeeRuleRow; versions: Record<string, unknown>[] }>(`/api/admin/fee-rules/${ruleId}`),
  taxRules: () => api.get<{ tax_rules: TaxRuleRow[] }>('/api/admin/tax-rules'),
  createTaxRule: (input: Record<string, unknown>) => api.post<{ tax_rule_id: string; version: { id: string } }>('/api/admin/tax-rules', input),
  activateTaxVersion: (ruleId: string, versionId: string) => api.post(`/api/admin/tax-rules/${ruleId}/versions/${versionId}/activate`),
  taxRule: (ruleId: string) => api.get<{ tax_rule: TaxRuleRow; versions: { id: string; version: number; status: string; name: string; rate_ppm: number }[] }>(`/api/admin/tax-rules/${ruleId}`),
};

// --- Phase 4: inventory -------------------------------------------------------

export interface InventoryUnitRow {
  unit_id: string;
  state: string;
  version: number;
  hold_id: string | null;
  section_id: string | null;
  row_id: string | null;
  ga_area_id: string | null;
  price_zone_id: string | null;
  cart_held: boolean;
  locked: boolean;
  seat_id: string | null;
  seat_number: string | null;
  row_label: string | null;
  section_code: string | null;
  table_label: string | null;
}

export interface InventoryHoldRow {
  id: string;
  name: string;
  notes: string | null;
  auto_release_at: string | null;
  closed_at: string | null;
  created_at: string;
  hold_class: string;
  inventory_state: string;
  owner_name: string | null;
  units: number;
}

export interface InventorySummary {
  initialized: boolean;
  performance_id: string;
  inventory_initialized_at?: string;
  inventory_version?: number;
  total?: number;
  cart_held?: number;
  by_state?: Record<string, number>;
  by_section?: { section_id: string | null; code: string | null; name: string | null; state: string; n: number }[];
  by_zone?: { price_zone_id: string | null; unit_kind: string; code: string | null; name: string | null; state: string; n: number }[];
  holds?: InventoryHoldRow[];
}

export interface InventorySelection {
  unit_ids?: string[];
  /** Whole tables, by id (D-129) — every seat at each one. */
  table_ids?: string[];
  section_id?: string;
  row_id?: string;
  price_zone_id?: string;
  ga_area_id?: string;
  quantity?: number;
  preempt_carts?: boolean;
  reason?: string | null;
}

const invPath = (eventId: string, performanceId: string, path = '') => `/api/admin/events/${eventId}/performances/${performanceId}/inventory${path}`;

export const inventoryEndpoints = {
  summary: (eventId: string, performanceId: string) => api.get<InventorySummary>(invPath(eventId, performanceId)),
  units: (eventId: string, performanceId: string) => api.get<{ inventory_version: number; cart_held: number; units: InventoryUnitRow[] }>(invPath(eventId, performanceId, '/units')),
  initialize: (eventId: string, performanceId: string, policy: 'POOL' | 'AVAILABLE') =>
    api.post<{ operation_id: string; counts: Record<string, unknown>; hydrated: number }>(invPath(eventId, performanceId, '/initialize'), { restricted_view_policy: policy }),
  report: (eventId: string, performanceId: string) => api.get<{ ok: boolean; identities: Record<string, boolean>; total: number; by_state: Record<string, number>; cart_held: number; live_tickets: number }>(invPath(eventId, performanceId, '/report')),
  history: (eventId: string, performanceId: string, unitId?: string) =>
    api.get<{ history: Record<string, unknown>[] }>(invPath(eventId, performanceId, `/history${unitId ? `?unit_id=${unitId}` : '?limit=100'}`)),
  placeHold: (eventId: string, performanceId: string, input: InventorySelection & { hold_class_code: string; name: string; notes?: string | null }) =>
    api.post<{ hold_id: string; units: number }>(invPath(eventId, performanceId, '/holds'), input),
  releaseHold: (eventId: string, performanceId: string, holdId: string, input: InventorySelection = {}) =>
    api.post<{ released: number; closed: boolean }>(invPath(eventId, performanceId, `/holds/${holdId}/release`), input),
  convertHold: (eventId: string, performanceId: string, holdId: string, holdClassCode: string) =>
    api.post<{ converted: number }>(invPath(eventId, performanceId, `/holds/${holdId}/convert`), { hold_class_code: holdClassCode }),
  change: (eventId: string, performanceId: string, action: 'kill' | 'unkill' | 'disable' | 'enable', input: InventorySelection) =>
    api.post<{ units: number }>(invPath(eventId, performanceId, `/${action}`), input),
  pools: (eventId: string, performanceId: string, input: InventorySelection & { pool: 'ADA' | 'COMPANION' | 'RESTRICTED_VIEW' | 'RELEASE' }) =>
    api.post<{ units: number }>(invPath(eventId, performanceId, '/pools'), input),
  remapZones: (eventId: string, performanceId: string) => api.post<{ remapped: number }>(invPath(eventId, performanceId, '/remap-zones')),
};

// --- Timed entry (D-86) -------------------------------------------------------

export interface TimedEntrySlot {
  id: string;
  performance_id: string;
  ticket_type_id: string | null;
  starts_at: string;
  ends_at: string | null;
  status: 'OPEN' | 'SALES_STOPPED' | 'CLOSED';
  capacity: number;
  units: number;
  sold: number;
  paid: number;
  comped: number;
  held: number;
  payment_pending: number;
  scanned: number;
  no_shows: number;
  available: number;
  committed: number;
  disabled: number;
  early_entry_minutes: number | null;
  late_entry_minutes: number | null;
  notes: string | null;
}

export interface GenerateSlotsInput {
  performance_ids: string[];
  ticket_type_id?: string | null;
  ga_area_id: string;
  name?: string | null;
  first_entry_time: string;
  last_entry_time: string;
  interval_minutes: number;
  slot_duration_minutes?: number | null;
  default_capacity: number;
  early_entry_minutes?: number | null;
  late_entry_minutes?: number | null;
  replace_existing?: boolean;
}

const timedPath = (eventId: string, suffix = '') => `/api/admin/events/${eventId}/timed-entry${suffix}`;

export const timedEntryEndpoints = {
  nights: (eventId: string) =>
    api.get<{ performances: { performance_id: string; starts_at: string; timezone: string; slots: number; capacity: number; sold: number }[] }>(timedPath(eventId)),
  slots: (eventId: string, performanceId: string) =>
    api.get<{ performance: { id: string; starts_at: string; timezone: string }; slots: TimedEntrySlot[] }>(timedPath(eventId, `/performances/${performanceId}`)),
  generate: (eventId: string, input: GenerateSlotsInput) =>
    api.post<{ generated: { performance_id: string; slots: number; admissions: number }[]; total_admissions: number }>(timedPath(eventId, '/generate'), input),
  updateSlot: (eventId: string, slotId: string, input: Record<string, unknown>) =>
    api.patch<{ slot: TimedEntrySlot; added: number; removed: number }>(timedPath(eventId, `/slots/${slotId}`), input),
  bulk: (eventId: string, input: { slot_ids: string[]; capacity?: number; status?: string; allow_below_committed?: boolean; reason?: string | null }) =>
    api.post<{ changed: { slot_id: string; added: number; removed: number }[]; refused: { slot_id: string; committed: number }[] }>(timedPath(eventId, '/slots/bulk'), input),
};

// --- Media library and sponsors (D-87) ---------------------------------------

export interface MediaAsset {
  id: string;
  title: string | null;
  alt_text: string | null;
  media_category_code: string | null;
  content_type: string;
  byte_size: number;
  width_px: number | null;
  height_px: number | null;
  original_filename: string | null;
  credit: string | null;
  usage_notes: string | null;
  created_at: string;
  uploaded_by: string | null;
  usage_count: number;
  url: string;
}

export interface MediaUsage {
  usage_kind: string;
  subject_type: string;
  subject_id: string;
  label: string;
}

export interface SponsorSummary {
  id: string;
  slug: string;
  name: string;
  display_name: string | null;
  website_url: string | null;
  default_tier_code: string | null;
  active: number;
  logos: number;
  events: number;
  logo_file_id: string | null;
}

export interface SponsorDetail extends Omit<SponsorSummary, 'logos' | 'events' | 'logo_file_id'> {
  organization_id: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  brand_guidelines: string | null;
  notes: string | null;
  version: number;
  logos: { id: string; file_id: string; variant_code: string; is_default: number; width_px: number | null; height_px: number | null; content_type: string; url: string }[];
  events: { event_id: string; title: string; tier_code: string | null }[];
}

export const mediaEndpoints = {
  categories: () => api.get<{ categories: { code: string; name: string; description: string | null; sort_order: number }[] }>('/api/admin/media/categories'),
  list: (filters: { category?: string | null; q?: string | null; unused?: boolean; limit?: number; offset?: number } = {}) => {
    const params = new URLSearchParams();
    if (filters.category) params.set('category', filters.category);
    if (filters.q) params.set('q', filters.q);
    if (filters.unused) params.set('unused', 'true');
    if (filters.limit) params.set('limit', String(filters.limit));
    if (filters.offset) params.set('offset', String(filters.offset));
    const query = params.toString();
    return api.get<{ assets: MediaAsset[]; total: number; limit: number; offset: number }>(`/api/admin/media${query ? `?${query}` : ''}`);
  },
  detail: (id: string) => api.get<{ asset: MediaAsset; usages: MediaUsage[] }>(`/api/admin/media/${id}`),
  /** The file itself is the body; what a person typed about it rides along in the query. */
  upload: async (file: File, meta: { title?: string | null; alt?: string | null; category?: string | null; credit?: string | null } = {}) => {
    const params = new URLSearchParams({ filename: file.name });
    if (meta.title) params.set('title', meta.title);
    if (meta.alt) params.set('alt', meta.alt);
    if (meta.category) params.set('category', meta.category);
    if (meta.credit) params.set('credit', meta.credit);
    return api.postRaw<{ asset: MediaAsset }>(`/api/admin/media?${params.toString()}`, file, file.type);
  },
  update: (id: string, input: Partial<Pick<MediaAsset, 'title' | 'alt_text' | 'media_category_code' | 'credit' | 'usage_notes'>>) =>
    api.patch<{ asset: MediaAsset }>(`/api/admin/media/${id}`, input),
  remove: (id: string) => api.delete<{ deleted: boolean }>(`/api/admin/media/${id}`),
};

export interface EventContentResponse {
  content: unknown;
  draft: { id: string; content: unknown; saved_at: string; staff: string | null } | null;
  sponsors: { id: string; name: string; display_name: string | null; website_url: string | null; tier_code: string | null; logos: { variant_code: string; file_id: string }[] }[];
}

export const contentEndpoints = {
  get: (eventId: string) => api.get<EventContentResponse>(`/api/admin/events/${eventId}/content`),
  revisions: (eventId: string) =>
    api.get<{ revisions: { id: string; kind: string; note: string | null; created_at: string; staff: string | null }[] }>(`/api/admin/events/${eventId}/content/revisions`),
  revision: (eventId: string, revisionId: string) =>
    api.get<{ revision: { id: string; created_at: string; content: unknown } }>(`/api/admin/events/${eventId}/content/revisions/${revisionId}`),
  saveDraft: (eventId: string, content: unknown) => api.put<{ saved_at: string }>(`/api/admin/events/${eventId}/content/draft`, { content }),
  discardDraft: (eventId: string) => api.delete<{ discarded: boolean }>(`/api/admin/events/${eventId}/content/draft`),
  publish: (eventId: string, content: unknown, note: string | null) => api.put<{ content: unknown; html: string }>(`/api/admin/events/${eventId}/content`, { content, note }),
  preview: (eventId: string, content: unknown) => api.post<{ html: string }>(`/api/admin/events/${eventId}/content/preview`, { content }),
};

import type { MergeField } from '@bre-ticketing/ticket-design';
import type { TermsSetting } from '@bre-ticketing/domain';

export interface TicketDesignSummary {
  id: string;
  code: string;
  version: number;
  name: string | null;
  description: string | null;
  status: string;
  canvas_kind: 'PDF' | 'BOCA';
  scope_type: 'ORGANIZATION' | 'VENUE' | 'EVENT' | 'PERFORMANCE';
  venue_id: string | null;
  event_id: string | null;
  performance_id: string | null;
  published_at: string | null;
  elements: number;
}

export interface DesignIssue {
  severity: 'ERROR' | 'WARNING';
  code: string;
  message: string;
  element_id?: string;
  scenario?: string;
}

export const designEndpoints = {
  // The registry is the package's, so the designer and the server agree on what a field is.
  fields: () => api.get<{ fields: MergeField[]; scenarios: { key: string; name: string; description: string }[] }>('/api/admin/ticket-designs/fields'),
  list: (filters: { canvas?: 'PDF' | 'BOCA'; event_id?: string; venue_id?: string } = {}) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) if (value) params.set(key, value);
    const query = params.toString();
    return api.get<{ designs: TicketDesignSummary[] }>(`/api/admin/ticket-designs${query ? `?${query}` : ''}`);
  },
  effective: (filters: { canvas: 'PDF' | 'BOCA'; performance_id?: string; event_id?: string; venue_id?: string }) => {
    const params = new URLSearchParams({ canvas: filters.canvas });
    for (const [key, value] of Object.entries(filters)) if (key !== 'canvas' && value) params.set(key, value);
    return api.get<{ design: (TicketDesignSummary & { design: unknown }) | null }>(`/api/admin/ticket-designs/effective?${params.toString()}`);
  },
  get: (id: string) =>
    api.get<{ design: TicketDesignSummary & { design: unknown }; versions: { id: string; version: number; status: string; published_at: string | null; published_by: string | null }[]; validation: { issues: DesignIssue[]; publishable: boolean } }>(
      `/api/admin/ticket-designs/${id}`,
    ),
  create: (input: { name: string; canvas_kind: 'PDF' | 'BOCA'; scope_type: string; venue_id?: string | null; event_id?: string | null; performance_id?: string | null; copy_from_id?: string | null }) =>
    api.post<{ design: TicketDesignSummary & { design: unknown } }>('/api/admin/ticket-designs', input),
  save: (id: string, design: unknown) => api.put<{ design: unknown; validation: { issues: DesignIssue[]; publishable: boolean } }>(`/api/admin/ticket-designs/${id}`, { design }),
  publish: (id: string) => api.post<{ design: TicketDesignSummary; issues: DesignIssue[] }>(`/api/admin/ticket-designs/${id}/publish`),
  retire: (id: string) => api.post<{ design: TicketDesignSummary }>(`/api/admin/ticket-designs/${id}/retire`),
  newVersion: (id: string) => api.post<{ design: TicketDesignSummary & { design: unknown } }>(`/api/admin/ticket-designs/${id}/versions`),
  testPrint: (id: string, input: { printer_id: string; scenario: string }) => api.post<{ job_id: string; printer: string; marked: string }>(`/api/admin/ticket-designs/${id}/test-print`, input),
};

/** A team a home game is played against (D-368). */
export interface OpponentRow {
  id: string;
  slug: string;
  name: string;
  short_name: string | null;
  abbreviation: string | null;
  logo_file_id: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  archived: boolean;
  /** What the storefront loads; `admin_logo_url` is the same image for a signed-in preview. */
  logo_url: string | null;
  admin_logo_url: string | null;
  /** Events naming it that are not archived. */
  games: number;
  next_game_at: string | null;
}

export const opponentEndpoints = {
  list: (all = false) => api.get<{ opponents: OpponentRow[] }>(`/api/admin/opponents${all ? '?all=1' : ''}`),
  create: (input: { name: string; slug?: string; short_name?: string | null; abbreviation?: string | null }) => api.post<{ opponent: OpponentRow }>('/api/admin/opponents', input),
  update: (id: string, input: { name?: string; slug?: string; short_name?: string | null; abbreviation?: string | null }) => api.patch<{ opponent: OpponentRow }>(`/api/admin/opponents/${id}`, input),
  archive: (id: string) => api.post<{ opponent: OpponentRow }>(`/api/admin/opponents/${id}/archive`),
  restore: (id: string) => api.post<{ opponent: OpponentRow }>(`/api/admin/opponents/${id}/restore`),
  uploadLogo: (id: string, file: File) => putRaw<{ opponent: OpponentRow }>(`/api/admin/opponents/${id}/logo`, file, file.type),
  removeLogo: (id: string) => api.del<{ opponent: OpponentRow }>(`/api/admin/opponents/${id}/logo`),
};

export const sponsorEndpoints = {
  tiers: () => api.get<{ tiers: { code: string; name: string; sort_order: number }[] }>('/api/admin/sponsors/tiers'),
  list: (includeInactive = false) => api.get<{ sponsors: SponsorSummary[] }>(`/api/admin/sponsors${includeInactive ? '?include_inactive=true' : ''}`),
  detail: (id: string) => api.get<{ sponsor: SponsorDetail }>(`/api/admin/sponsors/${id}`),
  create: (input: Record<string, unknown>) => api.post<{ sponsor: SponsorDetail }>('/api/admin/sponsors', input),
  update: (id: string, input: Record<string, unknown>) => api.patch<{ sponsor: SponsorDetail }>(`/api/admin/sponsors/${id}`, input),
  setLogo: (id: string, input: { file_id: string; variant_code: string; is_default?: boolean }) => api.put<{ sponsor: SponsorDetail }>(`/api/admin/sponsors/${id}/logos`, input),
  removeLogo: (id: string, variant: string) => api.delete<{ sponsor: SponsorDetail }>(`/api/admin/sponsors/${id}/logos/${variant}`),
  forEvent: (eventId: string) =>
    api.get<{ sponsors: { id: string; sponsor_id: string; tier_code: string | null; recognition_label: string | null; sort_order: number; slug: string; name: string; display_name: string | null; website_url: string | null; logo_file_id: string | null }[] }>(
      `/api/admin/events/${eventId}/sponsors`,
    ),
  attach: (eventId: string, input: { sponsor_id: string; tier_code?: string | null; recognition_label?: string | null; sort_order?: number }) =>
    api.post<{ added: boolean }>(`/api/admin/events/${eventId}/sponsors`, input),
  detach: (eventId: string, sponsorId: string) => api.delete<{ removed: boolean }>(`/api/admin/events/${eventId}/sponsors/${sponsorId}`),
};

/** "Continue with Google": a top-level navigation, because the flow leaves for Google and comes back (D-370). */
export const googleStartUrl = (returnTo: string) =>
  `/api/auth/google/start?surface=ADMIN&return_to=${encodeURIComponent(returnTo)}`;

/** What sign-in methods this deployment offers; public, so the login page can read it. */
export const signInOptions = () =>
  api.get<{ features: { google_sign_in?: boolean; email_sign_in?: boolean } }>('/api/public/config').then((config) => ({
    google: config.features.google_sign_in === true,
    email: config.features.email_sign_in !== false,
  }));

/** "Email me a sign-in link": always answers the same, whether or not the address is staff. */
export const requestSignInLink = (email: string, returnTo: string) =>
  api.post<{ ok: boolean }>('/api/auth/email/start', { email, surface: 'ADMIN', return_to: returnTo });

// --- Settings → Integrations (D-50) -------------------------------------------

export interface IntegrationFieldState {
  configured: boolean;
  hint: string | null;
  value: string | null;
  source: 'settings' | 'worker_secret' | null;
  updated_at: string | null;
}

export interface IntegrationProviderState {
  provider: string;
  name: string;
  description: string;
  fields: { key: string; label: string; secret: boolean; hint?: string }[];
  active_mode: 'TEST' | 'LIVE';
  mode_updated_at: string | null;
  live_allowed: boolean;
  modes: Record<'TEST' | 'LIVE', Record<string, IntegrationFieldState>>;
}

export interface FeatureFlag {
  key: string;
  name: string;
  description: string;
  default: boolean;
  /** Whether flipping it mid-sale is safe (D-306). */
  liveSafe: boolean;
  enabled: boolean;
  /** Somebody chose this, as opposed to it sitting at the code's default. */
  overridden: boolean;
  updated_at: string | null;
}

export type HardwareState = 'READY' | 'ATTENTION' | 'DOWN' | 'OFF';

export interface HardwareItem {
  id: string;
  kind: 'PRINT_BRIDGE' | 'BOX_OFFICE_TERMINAL' | 'SCANNER' | 'KIOSK' | 'PRINTER' | 'CARD_READER';
  name: string;
  venue_id: string | null;
  venue_name: string | null;
  state: HardwareState;
  detail: string;
  last_seen_at: string | null;
  /** Whether silence from this thing means anything at all (D-307). */
  reports_in: boolean;
  parent_id: string | null;
  extra: Record<string, string | number | null>;
}

export interface PrintQueueJob {
  id: string;
  printer_id: string;
  printer_name: string;
  job_type: string;
  status: string;
  attempt_count: number;
  max_attempts: number;
  failure_reason: string | null;
  ticket_id: string | null;
  order_id: string | null;
  order_number: string | null;
  created_at: string;
  leased_at: string | null;
  /** A bridge has it now, so it is the one job that must not be touched. */
  held: boolean;
}

export interface HardwareOverview {
  generated_at: string;
  items: HardwareItem[];
  queue: { jobs: PrintQueueJob[]; queued: number; leased: number; failed: number; oldest_queued_seconds: number | null };
  rules: { heartbeat_seconds: number; silent_after_seconds: number; lease_seconds: number };
}

export const hardwareEndpoints = {
  overview: () => api.get<HardwareOverview>('/api/admin/hardware'),
  testPage: (printerId: string) => api.post<{ print_job_id: string }>(`/api/admin/hardware/printers/${printerId}/test-page`, {}),
  retryJob: (jobId: string) => api.post<{ status: string }>(`/api/admin/hardware/jobs/${jobId}/retry`, {}),
  cancelJob: (jobId: string, reason: string) => api.post<{ status: string }>(`/api/admin/hardware/jobs/${jobId}/cancel`, { reason }),
};

export const settingsEndpoints = {
  flags: () => api.get<{ flags: FeatureFlag[] }>('/api/admin/settings/flags'),
  setFlag: (key: string, enabled: boolean) => api.put<{ flag: FeatureFlag }>(`/api/admin/settings/flags/${key}`, { enabled }),
  integrations: () => api.get<{ providers: IntegrationProviderState[]; production: boolean }>('/api/admin/settings/integrations'),
  updateIntegration: (provider: string, input: { mode?: 'TEST' | 'LIVE'; credentials?: Partial<Record<'TEST' | 'LIVE', Record<string, string | null>>> }) =>
    api.put<{ provider: IntegrationProviderState; changed: string[] }>(`/api/admin/settings/integrations/${provider}`, input),
  testIntegration: (provider: string, mode: 'TEST' | 'LIVE') =>
    api.post<{ ok: boolean; message: string; details?: Record<string, unknown> }>(`/api/admin/settings/integrations/${provider}/test`, { mode }),
  processingOffset: () => api.get<{ processing_offset: ProcessingOffsetSetting }>('/api/admin/settings/checkout/processing-offset'),
  updateProcessingOffset: (input: ProcessingOffsetSetting) =>
    api.put<{ processing_offset: ProcessingOffsetSetting }>('/api/admin/settings/checkout/processing-offset', input),
  accountCredit: () => api.get<{ policy: AccountCreditPolicy }>('/api/admin/settings/account-credit'),
  updateAccountCredit: (input: AccountCreditPolicy) => api.put<{ policy: AccountCreditPolicy }>('/api/admin/settings/account-credit', input),
  rainChecks: () => api.get<{ policy: RainCheckPolicy }>('/api/admin/settings/rain-checks'),
  updateRainChecks: (input: RainCheckPolicy) => api.put<{ policy: RainCheckPolicy }>('/api/admin/settings/rain-checks', input),
};

/** Account credit (D-372): null days means credit never expires. */
export interface AccountCreditPolicy {
  expires_after_days: number | null;
  staff_adjustments: boolean;
}

/** "Cover the processing fees?" at checkout (D-234): offered, ticked by default, and the rate. */
export interface ProcessingOffsetSetting {
  offered: boolean;
  default_on: boolean;
  /** Basis points, so 4% is 400 — no float anywhere near money. */
  basis_points: number;
}

// --- Donations (D-51) ------------------------------------------------------------

export interface DonationCampaignRow {
  id: string;
  slug: string;
  name: string;
  tagline: string | null;
  description_md: string | null;
  suggested_amounts: number[];
  min_amount_cents: number;
  allow_custom_amount: boolean;
  allow_recurring: boolean;
  offer_in_checkout: boolean;
  default_for_refunds: boolean;
  checkout_prompt: string | null;
  checkout_note: string | null;
  artwork_file_id: string | null;
  tax_deductible: boolean;
  receipt_note: string | null;
  /** A goal to give toward, and the thermometer on the donation page (D-83). */
  goal_cents: number | null;
  show_progress: boolean;
  progress_offset_cents: number;
  active: boolean;
  sort_order: number;
  gifts?: number;
  raised_cents?: number;
  recurring_active?: number;
  created_at: string;
  updated_at: string;
  /** This fund's own code, mapped to its QuickBooks account under QuickBooks (D-331). */
  accounting_code: string | null;
  /** The donor wall on the page (D-332). */
  show_donor_wall: boolean;
  wall_review_required: boolean;
  /** The page's blocks, for the editor (D-332). */
  content: unknown;
}

export interface DonationRow {
  id: string;
  campaign_id: string;
  campaign_name?: string;
  campaign_slug?: string;
  kind: 'ONE_TIME' | 'RECURRING' | 'CHECKOUT';
  status: string;
  amount_cents: number;
  interval: 'MONTH' | 'YEAR' | null;
  channel: string;
  customer_sub: string | null;
  donor_name: string | null;
  donor_email: string | null;
  anonymous: boolean;
  message: string | null;
  order_id: string | null;
  stripe_mode: 'TEST' | 'LIVE';
  stripe_payment_intent_id: string | null;
  stripe_subscription_id: string | null;
  payments_count?: number;
  paid_cents?: number;
  created_at: string;
  succeeded_at: string | null;
  canceled_at: string | null;
  paused_until?: string | null;
}

export interface DonationSummary {
  totals: { raised_cents: number; gifts: number; donors: number; recurring_active: number; recurring_monthly_cents: number } | null;
  by_month: { month: string; amount_cents: number; gifts: number }[];
}

/** One gift asking to be on a campaign's wall (D-338). */
export interface WallListingRow {
  donation_id: string;
  donor_name: string | null;
  donor_email: string | null;
  wall_name: string | null;
  wall_listing: 'NAME' | 'ANONYMOUS' | 'IN_MEMORY';
  in_memory_of: string | null;
  wall_hidden_at: string | null;
  wall_reviewed_at: string | null;
  total_cents: number;
  last_given_at: string;
}

export const donationEndpoints = {
  wall: (campaignId: string) => api.get<{ review_required: boolean; listings: WallListingRow[] }>(`/api/admin/donations/campaigns/${campaignId}/wall`),
  moderate: (donationId: string, input: { hidden?: boolean; reviewed?: true; wall_name?: string | null; in_memory_of?: string }) => api.patch<{ listing: unknown }>(`/api/admin/donations/wall/${donationId}`, input),
  campaigns: () => api.get<{ campaigns: DonationCampaignRow[] }>('/api/admin/donations/campaigns'),
  createCampaign: (input: Record<string, unknown>) => api.post<{ campaign: DonationCampaignRow }>('/api/admin/donations/campaigns', input),
  updateCampaign: (id: string, input: Record<string, unknown>) => api.patch<{ campaign: DonationCampaignRow }>(`/api/admin/donations/campaigns/${id}`, input),
  uploadArtwork: (id: string, file: File) => putRaw<{ campaign: DonationCampaignRow }>(`/api/admin/donations/campaigns/${id}/artwork`, file, file.type),
  removeArtwork: (id: string) => api.del<{ campaign: DonationCampaignRow }>(`/api/admin/donations/campaigns/${id}/artwork`),
  artworkUrl: (id: string) => `/api/admin/donations/campaigns/${id}/artwork`,
  /** The page's blocks (D-332). */
  saveContent: (id: string, doc: unknown) => api.put<{ campaign: DonationCampaignRow; html: string }>(`/api/admin/donations/campaigns/${id}/content`, { doc }),
  previewContent: (id: string, doc: unknown) => api.post<{ html: string }>(`/api/admin/donations/campaigns/${id}/content/preview`, { doc }),
  sponsors: () => api.get<{ sponsors: { id: string; name: string; display_name: string | null; tier_code: string | null; logos: { variant_code: string; file_id: string }[] }[] }>('/api/admin/donations/sponsors'),
  donations: (params: Record<string, string | undefined>) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) if (value) query.set(key, value);
    return api.get<{ donations: DonationRow[] }>(`/api/admin/donations${query.size ? `?${query}` : ''}`);
  },
  summary: () => api.get<DonationSummary>('/api/admin/donations/summary'),
  donation: (id: string) => api.get<{ donation: DonationRow; payments: { id: string; amount_cents: number; status: string; paid_at: string | null; stripe_invoice_id: string | null; stripe_payment_intent_id: string | null }[] }>(`/api/admin/donations/${id}`),
  cancelRecurring: (id: string, reason: string) => api.post<{ donation: DonationRow }>(`/api/admin/donations/${id}/cancel`, { reason }),
  pauseRecurring: (id: string, input: { resumes_on: string | null; reason?: string | null }) => api.post<{ donation: DonationRow }>(`/api/admin/donations/${id}/pause`, input),
  resumeRecurring: (id: string) => api.post<{ donation: DonationRow }>(`/api/admin/donations/${id}/resume`),
  changeRecurringAmount: (id: string, input: { amount_cents: number; reason?: string | null }) => api.post<{ donation: DonationRow }>(`/api/admin/donations/${id}/amount`, input),
};

// --- Orders (Phase 6) -----------------------------------------------------------

export interface OrderListRow {
  id: string;
  order_number: string;
  channel: string;
  status: string;
  customer_sub: string | null;
  customer_name_snapshot: string | null;
  customer_email_snapshot: string | null;
  business_date: string | null;
  cached_total_due_cents: number;
  cached_paid_cents: number;
  cached_refunded_cents: number;
  created_at: string;
  completed_at: string | null;
  ticket_count: number;
  event_titles: string | null;
  refund_requests: number;
  transferred_count: number;
  transfer_pending_count: number;
  /** D-71 */
  payment_status?: string;
  payment_due_at?: string | null;
  balance_due_cents?: number | null;
  /** The buyer's tags, as badges (D-358). */
  customer_tags?: CustomerTagBadge[];
}

export interface OrderDetail {
  id: string;
  order_number: string;
  channel: string;
  status: string;
  customer: { sub: string | null; name: string | null; email: string | null; phone: string | null; organization_name?: string | null };
  totals: { gross_cents: number; total_due_cents: number; paid_cents: number; refunded_cents: number };
  business_date: string | null;
  created_at: string;
  completed_at: string | null;
  expires_at: string | null;
  /** What this party asked for at checkout (D-166). Admin only. */
  accessibility_needs?: { code: string; name: string; description: string }[];
  /** What a refund could still reverse, even on an order that already reads REFUNDED (D-218). */
  refundable_remaining_cents?: number;
  /** Credit this order handed out that could still be taken back (D-215). */
  /** Credit a refund handed out that can still be taken back: a gift card (D-215) or account credit (D-372). */
  reversible_credits?: { refund_id: string; refund_number: string; gift_card_ref: string | null; customer_id: string | null; outstanding_cents: number }[];
  accessibility_note?: string | null;
  /** Notes (D-169), newest first. */
  notes?: OrderNote[];
  items: {
    id: string;
    item_type: string;
    status: string;
    performance_id: string | null;
    event_title_snapshot: string | null;
    performance_starts_at_snapshot: string | null;
    section_name_snapshot: string | null;
    row_label_snapshot: string | null;
    seat_number_snapshot: string | null;
    ga_area_name_snapshot: string | null;
    ticket_type_name_snapshot: string | null;
    description_snapshot: string;
    face_value_cents: number;
    discount_cents: number;
    comp_cents: number;
    selling_price_cents: number;
    total_cents: number | null;
    timezone: string | null;
    parent_order_item_id?: string | null;
    package_instance_id?: string | null;
    pass_id?: string | null;
    ticket: { id: string; status: string; short_code: string; credential_version: number; holder: { name: string | null; email: string | null; transferred: boolean } } | null;
  }[];
  order_fees_cents: number;
  delivery: { method: string | null; name: string | null; address: DeliveryAddress | null; fulfillment_printed_at: string | null };
  payments: { id: string; tender_type: string; status: string; amount_cents: number; captured_amount_cents: number; refunded_amount_cents: number; card_brand: string | null; card_last4: string | null; stripe_payment_intent_id: string | null; failure_message: string | null; captured_at: string | null }[];
  refunds: { id: string; refund_number: string; status: string; scope: string; reason_code: string; reason_text: string | null; disposition: 'TENDER' | 'DONATION'; donation_id: string | null; rejected_reason: string | null; cached_total_refund_cents: number; requested_at: string; completed_at: string | null }[];
  lines: { id: string; order_item_id: string | null; line_type: string; line_kind: string; amount_cents: number; revenue_owner_type: string; accounting_code: string; refundable: number; name_snapshot: string; original_line_id: string | null; business_date: string }[];
  events: { id: string; event_type: string; occurred_at: string; actor_type: string; actor_id: string | null; reason: string | null; data_json: string | null }[];
  transfers: { id: string; ticket_id: string; order_item_id: string; status: string; recipient_name_snapshot: string | null; recipient_email_snapshot: string | null; message: string | null; credential_version_before: number; credential_version_after: number | null; created_at: string; expires_at: string; accepted_at: string | null; declined_at: string | null; cancelled_at: string | null }[];
  exchanges: { id: string; status: string; channel: string; policy_code: string; reason: string | null; original_value_cents: number; new_value_cents: number; fee_cents?: number; adjustment_cents: number; amount_due_cents: number; amount_refunded_cents: number; refund_id: string | null; completed_at: string | null; staff_name: string | null; out: { ticket_id: string; short_code: string; replaced_by_ticket_id: string | null }[]; in: { ticket_id: string; short_code: string; order_item_id: string }[] }[];
}

export interface RefundRequestRow {
  id: string;
  refund_number: string;
  scope: string;
  reason_text: string | null;
  disposition: 'TENDER' | 'DONATION';
  donation_campaign_id: string | null;
  requested_by_actor_type: string;
  requested_at: string;
  order_item_ids: string[];
  /** What it would pay back if approved now, worked out fresh (D-277). */
  preview: RefundPreview;
}

export interface OrderEmailRow {
  id: string;
  template_code: string;
  status: 'REQUESTED' | 'SENDING' | 'SENT' | 'RETRYING' | 'FAILED' | 'SUPPRESSED' | string;
  to_email: string;
  subject_snapshot: string | null;
  attempts: number;
  last_error: string | null;
  requested_at: string;
  sent_at: string | null;
}

export interface TermsAgreement {
  terms_version_id: string;
  document_code: string;
  version: number;
  agreed_at: string;
  channel: string;
  ip_address: string | null;
}

export interface OrderDetailResponse {
  order: OrderDetail;
  refund_requests: RefundRequestRow[];
  printers: { id: string; name: string; venue_id: string }[];
  campaigns: { id: string; name: string; default_for_refunds: number }[];
  emails: OrderEmailRow[];
  /**
   * What this customer agreed to. **Empty means not recorded** (D-313) — an
   * order placed before terms were versioned has no row and never will.
   */
  terms_agreements: TermsAgreement[];
  /** What staff have written about this buyer (D-358). */
  customer_record?: CustomerAtSale;
}

export interface TermsVersionRow {
  id: string;
  document_code: string;
  version: number;
  status: 'DRAFT' | 'ACTIVE' | 'SUPERSEDED';
  body_md: string;
  change_note: string | null;
  effective_from: string | null;
  activated_at: string | null;
  superseded_at: string | null;
  created_at: string;
}

export interface AddOnProductRow {
  id: string;
  code: string;
  name: string;
  description: string | null;
  price_cents: number;
  event_id: string | null;
  max_per_order: number;
  /** Null means unlimited, which is a different thing from nought (D-314). */
  stock_limit: number | null;
  active: number;
  sort_order: number;
  channels: string[];
  sold: number;
}

export const addOnEndpoints = {
  list: () => api.get<{ add_ons: AddOnProductRow[] }>('/api/admin/add-ons'),
  create: (input: Record<string, unknown>) => api.post<{ id: string }>('/api/admin/add-ons', input),
  update: (id: string, input: Record<string, unknown>) => api.patch<{ ok: true }>(`/api/admin/add-ons/${id}`, input),
};

export const termsEndpoints = {
  list: () => api.get<{ documents: { code: string; name: string; required_at_checkout: number }[]; versions: TermsVersionRow[] }>('/api/admin/terms'),
  draft: (input: { document_code: string; body_md: string; change_note?: string | null }) => api.post<{ version: TermsVersionRow }>('/api/admin/terms', input),
  save: (id: string, input: { body_md?: string; change_note?: string | null }) => api.patch<{ ok: true }>(`/api/admin/terms/${id}`, input),
  activate: (id: string) => api.post<{ ok: true }>(`/api/admin/terms/${id}/activate`, {}),
};

export interface RefundResult {
  refund: { refund_number: string; refund_total_cents: number; disposition: 'TENDER' | 'DONATION'; donation_id: string | null; stripe: { ok: boolean; message: string | null } };
  released: { performance_id: string; ok: boolean; code?: string }[];
  order: OrderDetail;
}

export interface RefundEstimateLine {
  name: string;
  amount_cents: number;
  order_item_id: string | null;
  ticket_id: string | null;
}

export interface RefundEstimate {
  refund_total_cents: number;
  balance_due_cents: number;
  money_back_cents: number;
  receivable_relief_cents: number;
  item_count: number;
  whole_order: boolean;
  returning: RefundEstimateLine[];
  /** Non-refundable charges this refund is deliberately keeping: the answer to "why isn't it the full price?". */
  kept: RefundEstimateLine[];
  tenders: { payment_id: string; tender_type: string; label: string; amount_cents: number; note: string }[];
  notes: string[];
}

/** An estimate, or the reason there isn't one — both arrive as a 200 (D-277). */
export type RefundPreview = { ok: true; estimate: RefundEstimate } | { ok: false; code: string; message: string };

export const orderEndpoints = {
  /** Notes on an order (D-169), and voiding one ticket (D-170). */
  addNote: (id: string, input: { body: string; visibility: 'INTERNAL' | 'CUSTOMER' }) => api.post<{ notes: OrderNote[] }>(`/api/admin/orders/${id}/notes`, input),
  /** Withdraw a whole unpaid order: every ticket voided, seats released, packages closed (D-286). */
  voidOrder: (id: string, input: { void_reason: 'COMP_VOIDED' | 'ADMIN_VOID'; note?: string | null; release_seats?: boolean }) =>
    api.post<{ voided: { order_number: string; tickets_voided: { ticket_id: string; seat_released: boolean }[]; packages_closed: number; subscriptions_cancelled: number; seats_released: number; seats_stuck: number }; order: OrderDetail }>(`/api/admin/orders/${id}/void`, input),
  voidTicket: (id: string, ticketId: string, input: { void_reason: string; note?: string | null; release_seat?: boolean }) =>
    api.post<{ voided: { ticket_id: string; void_reason: string; seat_released: boolean } }>(`/api/admin/orders/${id}/tickets/${ticketId}/void`, input),
  list: (params: Record<string, string | undefined>) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) if (value) query.set(key, value);
    return api.get<{ orders: (OrderListRow & { refund_requests: number })[] }>(`/api/admin/orders${query.size ? `?${query}` : ''}`);
  },
  detail: (id: string) => api.get<OrderDetailResponse>(`/api/admin/orders/${id}`),
  refund: (id: string, input: { scope: 'FULL_ORDER' | 'TICKETS' | 'PACKAGE'; order_item_ids?: string[]; package_instance_id?: string | null; reason_code: string; reason_text?: string | null; nonrefundable_fee_override?: boolean; release_seats?: boolean; disposition: 'TENDER' | 'DONATION'; donation_campaign_id?: string | null }) =>
    api.post<RefundResult>(`/api/admin/orders/${id}/refunds`, input),
  /** What that refund would pay back, and to which tender, before taking it (D-277, D-278). */
  previewRefund: (id: string, input: { scope: 'FULL_ORDER' | 'TICKETS' | 'PACKAGE'; order_item_ids?: string[]; package_instance_id?: string | null; nonrefundable_fee_override?: boolean; disposition?: 'TENDER' | 'DONATION'; issue_credit?: boolean }) =>
    api.post<RefundPreview>(`/api/admin/orders/${id}/refunds/preview`, input),
  approveRefund: (id: string, refundId: string, input: { reason_text?: string | null; nonrefundable_fee_override?: boolean; release_seats?: boolean }) => api.post<RefundResult>(`/api/admin/orders/${id}/refunds/${refundId}/approve`, input),
  rejectRefund: (id: string, refundId: string, reason: string) => api.post<{ order: OrderDetail }>(`/api/admin/orders/${id}/refunds/${refundId}/reject`, { reason }),
  /** Void the gift card a refund issued and pay the same money to the card instead (D-215). */
  reverseCredit: (id: string, refundId: string, input: { reason_code?: string; reason_text?: string | null }) =>
    api.post<{ reversal: { reversed_cents: number; gift_card_ref: string | null; customer_id: string | null; refund_number: string | null; already_voided: boolean }; order: OrderDetail }>(`/api/admin/orders/${id}/refunds/${refundId}/reverse-credit`, input),
  reissue: (id: string, ticketId: string, reason: string) => api.post<{ reissued: { credential_version: number }; order: OrderDetail }>(`/api/admin/orders/${id}/tickets/${ticketId}/reissue`, { reason }),
  reissueAll: (id: string, reason: string) => api.post<{ reissued: { credential_version: number }[]; order: OrderDetail }>(`/api/admin/orders/${id}/reissue-all`, { reason }),
  reprint: (id: string, ticketId: string, printerId: string, reason: string) => api.post<{ reprinted: { credential_version: number; print_job_id: string }; order: OrderDetail }>(`/api/admin/orders/${id}/tickets/${ticketId}/reprint`, { printer_id: printerId, reason }),
  reprintAll: (id: string, printerId: string, reason: string) => api.post<{ reprinted: { credential_version: number; print_job_id: string }[]; order: OrderDetail }>(`/api/admin/orders/${id}/reprint-all`, { printer_id: printerId, reason }),
  cancel: (id: string) => api.post<{ released: boolean; order: OrderDetail }>(`/api/admin/orders/${id}/cancel`),
  /** Put a customer on an order that has none (D-252). */
  attachContact: (id: string, input: { sub?: string | null; name?: string | null; email?: string | null; phone?: string | null }) =>
    api.patch<{ order: OrderDetail }>(`/api/admin/orders/${id}/contact`, input),
  resendConfirmation: (id: string, toEmail: string | null) => api.post<{ email_message_id: string; emails: OrderEmailRow[] }>(`/api/admin/orders/${id}/resend-confirmation`, { to_email: toEmail }),
  ticketPdfUrl: (id: string, ticketId: string) => `/api/admin/orders/${id}/tickets/${ticketId}/pdf`,
};

// --- Phase 8: box office configuration, drawer shifts, ticket templates ------------------

export interface RegisterRow {
  id: string;
  venue_id: string;
  code: string;
  name: string;
  default_payment_terminal_id: string | null;
  default_ticket_printer_id: string | null;
  default_receipt_printer_id: string | null;
  allow_manual_card: number;
  blind_close: number;
  active: number;
}

export interface PrinterRow {
  id: string;
  register_id: string | null;
  print_bridge_device_id: string;
  bridge_name: string;
  bridge_last_seen_at: string | null;
  name: string;
  printer_model: 'BOCA_FGL' | 'EPSON_ESCPOS' | 'STAR_LINE';
  ticket_stock_id: string | null;
  connection: { kind: 'tcp' | 'usb' | 'file'; host?: string; port?: number; path?: string };
  dpi: number | null;
  last_status: string | null;
  last_status_at: string | null;
  active: number;
}

export interface TerminalRow {
  id: string;
  register_id: string | null;
  label: string;
  stripe_reader_id: string;
  simulated: number;
  livemode: number;
  device_model: string | null;
  serial_number: string | null;
  last_known_status: string | null;
  last_seen_at: string | null;
  active: number;
}

export interface BoxOfficeVenueConfig {
  registers: RegisterRow[];
  drawers: { id: string; register_id: string | null; code: string; name: string; active: number }[];
  printers: PrinterRow[];
  terminals: TerminalRow[];
  bridges: { id: string; name: string; status: string; last_seen_at: string | null }[];
  sessions: { id: string; register_id: string; register_name: string; status: string; device_name: string; opened_at: string; closed_at: string | null; business_date: string; opened_by_name: string; open_shifts: number }[];
  stocks: { id: string; code: string; name: string; width_mils: number; height_mils: number }[];
}

export interface ShiftRow {
  id: string;
  status: string;
  cashier_staff_account_id: string;
  cashier_name: string;
  drawer_name: string;
  register_name: string;
  venue_id: string;
  opened_at: string;
  closed_at: string | null;
  opening_float_cents: number;
  expected_close_cents: number | null;
  counted_close_cents: number | null;
  over_short_cents: number | null;
  original_counted_close_cents: number | null;
  corrected_at: string | null;
  correction_reason: string | null;
  business_date: string;
}

export interface ShiftReport {
  shift: ShiftRow;
  cashier_name: string | null;
  drawer: { name: string; code: string } | null;
  expected: { opening_float_cents: number; cash_sales_cents: number; cash_refunds_cents: number; paid_in_cents: number; paid_out_cents: number; expected_cents: number; cash_sale_count: number; cash_refund_count: number };
  payments: { id: string; order_id: string; order_number: string; amount_cents: number; cash_tendered_cents: number | null; change_given_cents: number | null; captured_at: string }[];
  refunds: { id: string; refund_number: string; order_id: string; order_number: string; amount_cents: number; completed_at: string }[];
  movements: { id: string; movement_type: string; direction: 'IN' | 'OUT'; amount_cents: number; reason: string; created_at: string; staff_name: string }[];
  counts: { count_type: string; denomination_cents: number; quantity: number; created_at: string }[];
}

export interface TicketTemplateRow {
  id: string;
  name: string;
  is_default: number;
  sponsor_logo_file_id: string | null;
  header_image_file_id: string | null;
  wallet_logo_file_id: string | null;
  wallet_hero_file_id: string | null;
  accent_hex: string | null;
  sponsor_line: string | null;
  terms_text: string | null;
  show_price: number;
  active: number;
  created_at: string;
  updated_at: string;
}

export const boxOfficeEndpoints = {
  venue: (venueId: string) => api.get<BoxOfficeVenueConfig>(`/api/admin/box-office/venues/${venueId}`),
  createRegister: (venueId: string, input: Record<string, unknown>) => api.post<{ register: RegisterRow }>(`/api/admin/box-office/venues/${venueId}/registers`, input),
  updateRegister: (id: string, input: Record<string, unknown>) => api.patch<{ register: RegisterRow }>(`/api/admin/box-office/registers/${id}`, input),
  createDrawer: (venueId: string, input: Record<string, unknown>) => api.post(`/api/admin/box-office/venues/${venueId}/drawers`, input),
  createPrinter: (venueId: string, input: Record<string, unknown>) => api.post<{ printer: { id: string } }>(`/api/admin/box-office/venues/${venueId}/printers`, input),
  updatePrinter: (id: string, input: Record<string, unknown>) => api.patch(`/api/admin/box-office/printers/${id}`, input),
  testPage: (printerId: string) => api.post<{ print_job_id: string }>(`/api/admin/box-office/printers/${printerId}/test-page`),
  printerJobs: (printerId: string) => api.get<{ jobs: { id: string; job_type: string; status: string; attempt_count: number; failure_reason: string | null; created_at: string; printed_at: string | null }[] }>(`/api/admin/box-office/printers/${printerId}/jobs`),
  registerTerminal: (venueId: string, input: { label: string; registration_code: string; register_id?: string | null }) => api.post<{ terminal: TerminalRow }>(`/api/admin/box-office/venues/${venueId}/terminals`, input),
  updateTerminal: (id: string, input: Record<string, unknown>) => api.patch(`/api/admin/box-office/terminals/${id}`, input),
  forceClose: (sessionId: string, reason: string) => api.post(`/api/admin/box-office/sessions/${sessionId}/force-close`, { reason }),
  shifts: (params: { venue_id?: string; date?: string }) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) if (value) query.set(key, value);
    return api.get<{ shifts: ShiftRow[] }>(`/api/admin/box-office/shifts${query.size ? `?${query}` : ''}`);
  },
  shift: (id: string) => api.get<ShiftReport>(`/api/admin/box-office/shifts/${id}`),
  correctShift: (id: string, input: { counted_close_cents: number; reason: string }) => api.post<{ shift: ShiftRow; report: ShiftReport }>(`/api/admin/box-office/shifts/${id}/correct`, input),
  /**
   * One show, or a window of instants meaning "the night" (item 75, D-310).
   *
   * The response says which performances the window turned out to cover, so the
   * screen can show that rather than making somebody trust the dates.
   */
  fulfillment: (params: { performance_id?: string; from?: string; to?: string; venue_id?: string; method: 'MAIL' | 'WILL_CALL'; include_printed?: boolean }) => {
    const query = new URLSearchParams({ method: params.method });
    if (params.performance_id) query.set('performance_id', params.performance_id);
    if (params.from && params.to) {
      query.set('from', params.from);
      query.set('to', params.to);
    }
    if (params.venue_id) query.set('venue_id', params.venue_id);
    if (params.include_printed) query.set('include_printed', '1');
    return api.get<{ performances: FulfillmentPerformance[]; orders: FulfillmentOrder[] }>(`/api/admin/box-office/fulfillment?${query.toString()}`);
  },
  printFulfillment: (input: { performance_ids: string[]; method: 'MAIL' | 'WILL_CALL'; printer_id: string; order_ids?: string[]; include_printed: boolean }) => api.post<FulfillmentBatch>('/api/admin/box-office/fulfillment/print', input),
};

// --- Phase 9: admissions ------------------------------------------------------------

export interface GateRow {
  id: string;
  code: string;
  name: string;
  active: number;
}

export interface ZoneRow {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  active: number;
}

export interface PerformanceAdmissions {
  performance: { id: string; venue_id: string; event_id: string };
  gates: GateRow[];
  zones: ZoneRow[];
  gate_zones: { gate_id: string; access_zone_id: string }[];
  rules: { id: string; access_zone_id: string; ticket_type_id: string | null; section_id: string | null; ga_area_id: string | null; price_zone_id: string | null }[];
}

export interface DoorPace {
  per_minute: number | null;
  per_minute_overall: number | null;
  remaining: number;
  first_admitted_at: string | null;
  last_admitted_at: string | null;
  minutes_to_clear: number | null;
  /** Why there is no estimate. Shown instead of a blank, so it can be read (D-309). */
  projection_withheld: string | null;
  quiet_gates: { gate: string; admitted: number; silent_minutes: number }[];
}

export interface SlotProgress {
  slot_id: string;
  starts_at: string;
  ends_at: string | null;
  capacity: number;
  expected: number;
  arrived: number;
  current: boolean;
}

export interface AssistiveDevice {
  id: string;
  venue_id: string;
  asset_tag: string;
  feature_code: string;
  feature_name: string | null;
  model: string | null;
  condition: string;
  lendable: number;
  notes: string | null;
  retired_at: string | null;
  /** Who has it right now. What makes the list useful at a window (D-317). */
  out_on_loan: { loan_id: string; borrower_name: string; lent_at: string } | null;
}

export interface OutstandingLoan {
  id: string;
  asset_tag: string;
  borrower_name: string;
  security_held: string | null;
  lent_at: string;
  hours_out: number;
  overdue: boolean;
}

export const assistiveDeviceEndpoints = {
  forVenue: (venueId: string) => api.get<{ devices: AssistiveDevice[] }>(`/api/admin/assistive-devices/venues/${venueId}`),
  add: (venueId: string, input: { asset_tag: string; feature_code: string; model?: string | null }) => api.post<{ id: string }>(`/api/admin/assistive-devices/venues/${venueId}`, input),
  update: (id: string, input: { condition?: string; notes?: string | null; retired?: boolean }) => api.patch<{ ok: true }>(`/api/admin/assistive-devices/${id}`, input),
  lend: (input: { assistive_device_id: string; performance_id?: string | null; borrower_name: string; security_held?: string | null }) =>
    api.post<{ loan: { id: string } }>('/api/admin/assistive-devices/loans', input),
  takeBack: (loanId: string, input: { condition: string; notes?: string | null }) => api.post<{ loan: { id: string } }>(`/api/admin/assistive-devices/loans/${loanId}/return`, input),
  outstanding: (params: { venue_id?: string; performance_id?: string }) => {
    const query = new URLSearchParams(Object.entries(params).filter(([, value]) => Boolean(value)) as [string, string][]);
    return api.get<{ loans: OutstandingLoan[] }>(`/api/admin/assistive-devices/loans/outstanding?${query.toString()}`);
  },
};

export interface DoorDashboard {
  performance_id: string;
  computed_at: string;
  totals: { live_tickets: number; admitted: number; inside: number; scans: number };
  by_result: { result: string; count: number }[];
  by_gate: { gate_id: string | null; gate: string | null; admitted: number; rejected: number; re_scans: number }[];
  by_ticket_type: { ticket_type: string | null; live: number; admitted: number }[];
  arrivals: { bucket: string; admitted: number }[];
  recent_rejections: { id: string; at: string; result: string; reason_text: string | null; gate: string | null; device: string; seat_label: string | null; short_code: string | null; offline: number }[];
  bundles: { id: string; device_id: string; device_name: string; bundle_version: number; ticket_count: number; generated_at: string; expires_at: string; revoked_at: string | null }[];
  /** What the people coming tonight asked for at checkout (D-166). */
  access_requests: { order_number: string; customer: string | null; needs: string; note: string | null; seats: string | null; tickets: number; wheelchair_spaces: number }[];
  /** How fast the line is moving, and when it clears (item 72, D-309). */
  pace: DoorPace;
  slots: SlotProgress[];
  /** Receivers lent tonight (item 250, D-317). */
  assistive_devices: { lent: number; returned: number; still_out: number };
}

/** The guest list for a performance (D-199). Comps, because a guest holds a real comp ticket. */
export interface GuestListParty {
  order_id: string;
  order_number: string;
  name: string | null;
  email: string | null;
  reason: string | null;
  reason_code: string | null;
  tickets: number;
  arrived: number;
  seats: string[];
}

export interface GuestList {
  performance_id: string;
  generated_at: string;
  parties: GuestListParty[];
  totals: { parties: number; tickets: number; arrived: number };
}

export const admissionEndpoints = {
  venue: (venueId: string) => api.get<{ gates: GateRow[]; zones: ZoneRow[] }>(`/api/admin/admissions/venues/${venueId}`),
  createGate: (venueId: string, input: { code: string; name: string }) => api.post<{ gate: GateRow }>(`/api/admin/admissions/venues/${venueId}/gates`, input),
  updateGate: (id: string, input: Record<string, unknown>) => api.patch<{ gate: GateRow }>(`/api/admin/admissions/gates/${id}`, input),
  createZone: (venueId: string, input: { code: string; name: string }) => api.post<{ zone: ZoneRow }>(`/api/admin/admissions/venues/${venueId}/zones`, input),
  updateZone: (id: string, input: Record<string, unknown>) => api.patch<{ zone: ZoneRow }>(`/api/admin/admissions/zones/${id}`, input),
  performance: (id: string) => api.get<PerformanceAdmissions>(`/api/admin/admissions/performances/${id}`),
  /** Who should be let in (D-199): the comps, by surname, with who has arrived. */
  guestList: (id: string) => api.get<{ guest_list: GuestList }>(`/api/admin/admissions/performances/${id}/guest-list`),
  setGateZones: (id: string, gates: { gate_id: string; access_zone_ids: string[] }[]) => api.put(`/api/admin/admissions/performances/${id}/gate-zones`, { gates }),
  setAccessRules: (id: string, rules: { access_zone_id: string; ticket_type_id?: string | null }[]) => api.put(`/api/admin/admissions/performances/${id}/access-rules`, { rules }),
  dashboard: (id: string) => api.get<{ dashboard: DoorDashboard }>(`/api/admin/admissions/performances/${id}/dashboard`),
  revokeBundle: (id: string) => api.post(`/api/admin/admissions/bundles/${id}/revoke`),
};

// --- Phase 10: reports --------------------------------------------------------------

export interface ReportColumn {
  key: string;
  label: string;
  kind: 'text' | 'int' | 'cents' | 'pct' | 'date' | 'datetime';
}

export interface DrillSpec {
  kind: 'tickets' | 'lines' | 'units' | 'scans' | 'ledger';
  filters: Record<string, string | number | boolean | null>;
}

export interface ReportResult {
  report_type: string;
  name: string;
  filters: Record<string, unknown>;
  as_of: string;
  columns: ReportColumn[];
  rows: (Record<string, unknown> & { drill?: DrillSpec })[];
  totals: Record<string, number> | null;
}

export interface DrillResult {
  kind: DrillSpec['kind'];
  rows: Record<string, unknown>[];
  next_cursor: string | null;
}

export interface EventAudit {
  event: { id: string; title: string; slug: string; accounting_mode: string; venue_name: string };
  performance: { id: string; starts_at: string; timezone: string } | null;
  as_of: string;
  inventory: { capacity: number; rows: { section: string | null; price_zone: string | null; state: string; hold_class: string | null; units: number }[] };
  sales: { rows: { ticket_type: string | null; price_zone: string | null; face_value_cents: number; sales_channel: string; sold: number; comps: number; refunded: number; face_total_cents: number; discount_cents: number; comp_cents: number; net_cents: number }[]; totals: { sold: number; comps: number; refunded: number; face_total_cents: number; discount_cents: number; comp_cents: number; net_cents: number } };
  fees: { rows: { name: string; line_type: string; sales_channel: string; owner: string; count: number; charged_cents: number; reversed_cents: number; net_cents: number }[] };
  tax: { rows: { name: string; charged_cents: number; reversed_cents: number; net_cents: number }[] };
  payments: { rows: { tender_type: string; card_brand: string | null; sales_channel: string; collected_cents: number; refunded_cents: number; net_cents: number }[] };
  payment_status?: { paid_orders: number; partially_paid_orders: number; unpaid_orders: number; other_orders: number; total_order_value_cents: number; collected_cents: number; outstanding_cents: number };
  accounting: { rows: { accounting_code: string; owner: string; debit_cents: number; credit_cents: number; net_cents: number }[]; balanced: boolean };
  attendance: { live_tickets: number; sold: number; comps: number; admitted: number; comps_admitted: number; re_scans: number; no_shows: number; attendance_pct: number | null };
  identities: { id: number; name: string; left_label: string; left: number; right_label: string; right: number; ok: boolean; drill: DrillSpec }[];
  projections: { projection: string; scope_key: string; drift: boolean; stored_rows: number; computed_rows: number; repaired: boolean }[];
}

export interface ReportScheduleRow {
  id: string;
  name: string;
  report_type: string;
  frequency: string;
  local_time: string | null;
  timezone: string;
  event_id: string | null;
  event_title: string | null;
  enabled: number;
  next_run_at: string | null;
  last_run_at: string | null;
  formats: string[];
  recipients: string[];
  runs: number;
}

export interface ReportRunRow {
  id: string;
  report_type: string;
  schedule_id: string | null;
  status: string;
  params: { filters: Record<string, unknown>; formats: string[] };
  requested_by: string | null;
  row_count: number | null;
  error: string | null;
  created_at: string;
  finished_at: string | null;
  formats: string[];
}

// --- Finance: Stripe payout reconciliation (D-64) ------------------------------------------------

export interface PayoutRow {
  id: string;
  stripe_payout_id: string;
  stripe_status: string;
  amount_cents: number;
  currency: string;
  arrival_date: string;
  stripe_created_at: string;
  automatic: number;
  destination_last4: string | null;
  reconciliation_status: string;
  gross_charges_cents: number;
  refunds_cents: number;
  disputes_cents: number;
  fees_cents: number;
  adjustments_cents: number;
  other_cents: number;
  net_cents: number;
  ledger_expected_net_cents: number | null;
  difference_cents: number | null;
  balance_transaction_count: number;
  matched_count: number;
  reconciled_at: string | null;
  reconciled_by?: string | null;
  error: string | null;
}

export interface PayoutTransactionRow {
  id: string;
  stripe_balance_transaction_id: string;
  type: string;
  reporting_category: string;
  source_id: string | null;
  amount_cents: number;
  fee_cents: number;
  net_cents: number;
  description: string | null;
  available_on: string;
  stripe_created_at: string;
  reconciliation_status: string;
  link_type: string | null;
  payment_id: string | null;
  refund_payment_id: string | null;
  dispute_id: string | null;
  order_id: string | null;
  order_number: string | null;
  refund_number: string | null;
  event_title: string | null;
}

export const exchangePolicyEndpoints = {
  list: () => api.get<{ policies: ExchangePolicyRow[] }>('/api/admin/exchange-policies'),
  create: (input: Record<string, unknown>) => api.post<{ policy: ExchangePolicyRow }>('/api/admin/exchange-policies', input),
  update: (id: string, input: Record<string, unknown>) => api.patch<{ policy: ExchangePolicyRow }>(`/api/admin/exchange-policies/${id}`, input),
};

export interface AdminExchangeOptions {
  event_id: string;
  performances: { id: string; starts_at: string; status: string; manifest_id: string }[];
  ticket_types: { id: string; code: string; name: string }[];
  sections: { performance_id: string; id: string; name: string }[];
  policy: { fee_cents: number; downgrade: 'REFUND' | 'FORFEIT'; name: string | null };
  held: { performance_id: string; unit_id: string }[];
}

export interface AdminExchangeQuote {
  out: { ticket_id: string; seat_label: string; ticket_type: string | null; performance_starts_at: string | null; value_cents: number }[];
  in: { unit_id: string; performance_id: string; seat_label: string; ticket_type: string | null; performance_starts_at: string; value_cents: number }[];
  original_value_cents: number;
  new_value_cents: number;
  fee_cents: number;
  difference_cents: number;
  downgrade: 'REFUND' | 'FORFEIT';
  policy_fee_cents: number;
}

export const adminExchangeEndpoints = {
  options: (orderId: string, ticketIds: string[]) => api.get<AdminExchangeOptions>(`/api/admin/orders/${orderId}/exchanges/options?ticket_ids=${encodeURIComponent(ticketIds.join(','))}`),
  hold: (orderId: string, input: Record<string, unknown>) => api.post<{ held: string[]; expires_at: string; held_total: number }>(`/api/admin/orders/${orderId}/exchanges/hold`, input),
  release: (orderId: string) => api.del<{ released: boolean }>(`/api/admin/orders/${orderId}/exchanges/hold`),
  quote: (orderId: string, ticketIds: string[], waiveFee: boolean) => api.post<{ quote: AdminExchangeQuote }>(`/api/admin/orders/${orderId}/exchanges/quote`, { ticket_ids: ticketIds, waive_fee: waiveFee }),
  card: (orderId: string, amountCents: number) => api.post<{ payment_intent_id: string; client_secret: string | null; amount_cents: number; publishable_key: string | null }>(`/api/admin/orders/${orderId}/exchanges/card`, { amount_cents: amountCents }),
  complete: (orderId: string, input: Record<string, unknown>) => api.post<{ exchange: { exchange_id: string; amount_due_cents: number; amount_refunded_cents: number; adjustment_cents: number; fee_cents: number; new_ticket_ids: string[]; refund: { refund_number: string; stripe: { ok: boolean; message: string | null } } | null } }>(`/api/admin/orders/${orderId}/exchanges`, input),
};

export const financeEndpoints = {
  payouts: (status?: string) => api.get<{ payouts: PayoutRow[]; accounting_codes: { code: string; name: string; family: string }[] }>(`/api/admin/finance/payouts${status ? `?status=${status}` : ''}`),
  payout: (id: string) => api.get<{ payout: PayoutRow; transactions: PayoutTransactionRow[]; journal: { id: string; business_date: string } | null }>(`/api/admin/finance/payouts/${id}`),
  sync: (since_days: number) => api.post<{ synced: { payouts: number; transactions: number; matched: number } }>('/api/admin/finance/payouts/sync', { since_days }),
  resync: (id: string) => api.post<{ payout: PayoutRow; transactions: PayoutTransactionRow[] }>(`/api/admin/finance/payouts/${id}/resync`),
  signOff: (id: string) => api.post<{ payout: PayoutRow }>(`/api/admin/finance/payouts/${id}/sign-off`),
  classify: (transactionId: string, input: { accounting_code: string; reason: string }) => api.post<{ payout: PayoutRow }>(`/api/admin/finance/transactions/${transactionId}/classify`, input),
};

export const reportEndpoints = {
  catalog: () => api.get<{ reports: { code: string; name: string; permission: string; requires: string[] }[]; formats: string[]; projections: string[] }>('/api/admin/reports/catalog'),
  run: (reportType: string, filters: Record<string, unknown>) => api.post<{ result: ReportResult }>('/api/admin/reports/run', { report_type: reportType, filters }),
  exportUrl: (reportType: string, format: string, filters: Record<string, unknown>) => `/api/admin/reports/export?report_type=${encodeURIComponent(reportType)}&format=${format}&filters=${encodeURIComponent(JSON.stringify(filters))}`,
  audit: (eventId: string, performanceId: string | null) => api.get<{ audit: EventAudit }>(`/api/admin/reports/audit?event_id=${encodeURIComponent(eventId)}${performanceId ? `&performance_id=${encodeURIComponent(performanceId)}` : ''}`),
  drill: (input: DrillSpec & { cursor?: string | null; limit?: number }) => api.post<DrillResult>('/api/admin/reports/drill', input),
  recompute: (performanceId: string) => api.post<{ rows: Record<string, number> }>('/api/admin/reports/projections/recompute', { performance_id: performanceId }),
  createRun: (input: { report_type: string; filters: Record<string, unknown>; formats: string[] }) => api.post<{ run_id: string; created: boolean }>('/api/admin/reports/runs', input),
  runs: () => api.get<{ runs: ReportRunRow[] }>('/api/admin/reports/runs'),
  runFileUrl: (runId: string, format: string) => `/api/admin/reports/runs/${runId}/files/${format}`,
  schedules: () => api.get<{ schedules: ReportScheduleRow[]; frequencies: string[] }>('/api/admin/reports/schedules'),
  createSchedule: (input: Record<string, unknown>) => api.post<{ schedule_id: string; schedules: ReportScheduleRow[] }>('/api/admin/reports/schedules', input),
  setScheduleEnabled: (id: string, enabled: boolean) => api.patch<{ schedules: ReportScheduleRow[] }>(`/api/admin/reports/schedules/${id}`, { enabled }),
  runScheduleNow: (id: string) => api.post<{ run_id: string }>(`/api/admin/reports/schedules/${id}/run-now`),
};

// --- Phase 12: packages and customers ----------------------------------------------------------

export interface PackageComponentRow {
  id: string;
  event_id: string | null;
  /** `event_id`, or the event its date belongs to when the component names only a date (D-384). */
  resolved_event_id: string | null;
  performance_id: string | null;
  ticket_type_id: string | null;
  quantity: number;
  required: number;
  choice_group: string | null;
  fixed_allocation_cents: number | null;
  event_title: string | null;
  performance_starts_at: string | null;
  timezone: string | null;
  ticket_type_name: string | null;
  sellable: boolean;
}

export interface PackageRow {
  id: string;
  code: string;
  name: string;
  description: string | null;
  package_kind: 'FIXED_PACKAGE' | 'CHOOSE_YOUR_OWN' | 'SEASON_TICKET' | 'TABLE_SUBSCRIPTION' | 'MEMBERSHIP_PASS';
  status: 'DRAFT' | 'ON_SALE' | 'OFF_SALE' | 'ARCHIVED';
  season_id: string | null;
  series_id: string | null;
  allocation_method: 'PROPORTIONAL_TO_FACE_VALUE' | 'EQUAL_SPLIT' | 'FIXED_AMOUNTS';
  min_selections: number | null;
  max_selections: number | null;
  same_seat_every_performance: number;
  renewal_enabled: number;
  renewal_deadline: string | null;
  renews_package_product_id: string | null;
  pass_valid_from: string | null;
  pass_valid_to: string | null;
  pass_admissions_per_performance: number;
  pass_total_redemptions: number | null;
  /** A pass that covers every regular-season game of its season (D-373). */
  pass_covers_season?: number;
  pass_ticket_type_code?: string | null;
  max_per_order: number;
  components: PackageComponentRow[];
  price_cents: number | null;
  prices: { channel: string | null; price_cents: number }[];
  face_total_cents: number | null;
  season: { id: string; code: string; name: string } | null;
  previous: { id: string; code: string; name: string } | null;
  /** The seating areas it is sold in, by price zone code (D-384); empty is anywhere. */
  price_zone_codes: string[];
  areas: { code: string; name: string }[];
  holders: { active: number; pending: number };
  renewals: { offered: number; expires_at: string | null };
}

export interface PackageInstanceRow {
  id: string;
  status: string;
  created_at: string;
  customer_sub: string | null;
  order_number: string;
  order_id: string;
  customer_name: string | null;
  customer_email: string | null;
  tickets: number;
  seats: string | null;
  subscription_status: string | null;
  renewal_offer_expires_at: string | null;
}

export const packageEndpoints = {
  list: () => api.get<{ packages: PackageRow[] }>('/api/admin/packages'),
  detail: (id: string) => api.get<{ package: PackageRow; instances: PackageInstanceRow[] }>(`/api/admin/packages/${id}`),
  create: (input: Record<string, unknown>) => api.post<{ package: PackageRow }>('/api/admin/packages', input),
  update: (id: string, input: Record<string, unknown>) => api.patch<{ package: PackageRow }>(`/api/admin/packages/${id}`, input),
  setComponents: (id: string, input: { components: Record<string, unknown>[] }) => api.put<{ package: PackageRow }>(`/api/admin/packages/${id}/components`, input),
  setPrices: (id: string, input: { prices: { channel: string | null; price_cents: number }[] }) => api.put<{ package: PackageRow }>(`/api/admin/packages/${id}/prices`, input),
  setStatus: (id: string, status: string) => api.post<{ package: PackageRow }>(`/api/admin/packages/${id}/status`, { status }),
  offerRenewals: (id: string, expires_at: string) => api.post<{ outcome: { offered: number; held_units: number; skipped: { instance_id: string; reason: string }[] }; package: PackageRow }>(`/api/admin/packages/${id}/renewals/offer`, { expires_at }),
  lapseRenewals: (id: string) => api.post<{ outcome: { released_units: number; lapsed_instances: number }; package: PackageRow }>(`/api/admin/packages/${id}/renewals/lapse`),
  seasons: () => api.get<{ seasons: { id: string; code: string; name: string; starts_on: string; ends_on: string }[] }>('/api/admin/packages/seasons'),
  /** The price zones its events are sold in, and which the package is limited to (D-384). */
  areas: (id: string) => api.get<{ price_zone_codes: string[]; choices: { code: string; name: string; events: number; reserved: number }[] }>(`/api/admin/packages/${id}/areas`),
  setAreas: (id: string, price_zone_codes: string[]) => api.put<{ price_zone_codes: string[]; package: PackageRow }>(`/api/admin/packages/${id}/areas`, { price_zone_codes }),
  createSeason: (input: { code: string; name: string; starts_on: string; ends_on: string }) => api.post<{ season: { id: string; code: string; name: string } }>('/api/admin/packages/seasons', input),
};

export interface CustomerSummaryRow {
  key: string;
  customer_sub: string | null;
  name: string | null;
  email: string | null;
  phone: string | null;
  orders: number;
  tickets: number;
  spend_cents: number;
  refunded_cents: number;
  donations_cents: number;
  first_order_at: string | null;
  last_order_at: string | null;
  events: number;
  admitted: number;
  /** Only on search results (D-358). */
  tags?: CustomerTagBadge[];
  /** What they bought in the previous system (D-364); only on search results. */
  history_orders?: number;
  history_spend_cents?: number;
}

/** One order from the previous system's sales history (D-364). */
export interface HistoricalOrder {
  order_number: string;
  placed_on: string;
  name: string | null;
  email: string | null;
  channel: string | null;
  payment_method: string | null;
  status: 'COMPLETED' | 'REFUNDED' | 'COMP';
  tickets: number;
  total_cents: number;
  gift_cents: number;
  surcharge_cents: number;
  items: { kind: 'TICKET' | 'GIFT' | 'SURCHARGE' | 'OTHER'; name: string; starts_at: string | null; ticket_type: string | null; quantity: number; amount_cents: number }[];
}

export interface HistoryTotals {
  orders: number;
  tickets: number;
  spend_cents: number;
  gift_cents: number;
  first_order_on: string | null;
  last_order_on: string | null;
}

export interface HistoryPreview {
  file_name: string;
  first_sale_date: string | null;
  last_sale_date: string | null;
  lines: number;
  orders: number;
  people: number;
  tickets: number;
  totals: { TICKET: number; GIFT: number; SURCHARGE: number; OTHER: number; net: number };
  shows: { name: string; starts_at: string; tickets: number; moved_over: boolean }[];
  unclassified: { item_name: string; lines: number; first_line: number }[];
  problems: { code: string; message: string; line?: number }[];
  ready: boolean;
}

export interface HistoryFileRow {
  id: string;
  file_name: string;
  first_sale_date: string;
  last_sale_date: string;
  line_count: number;
  order_count: number;
  net_cents: number;
  imported_at: string;
  imported_by: string | null;
}

export const historyEndpoints = {
  preview: (input: { file_name: string; csv: string }) => api.post<HistoryPreview>('/api/admin/history/preview', input),
  import: (input: { file_name: string; csv: string }) => api.post<{ file_id: string; preview: HistoryPreview }>('/api/admin/history/import', input),
  files: () => api.get<{ files: HistoryFileRow[] }>('/api/admin/history/files'),
  classify: (input: { item_name: string; line_kind: 'GIFT' | 'SURCHARGE' | 'OTHER' }) => api.post<{ item_name: string; line_kind: string }>('/api/admin/history/items', input),
};

export interface GivingYear {
  year: number;
  gifts: number;
  total_cents: number;
  deductible_cents: number;
}

/**
 * The giving half of a donor's record (item 28, D-312).
 *
 * Summed by the year-end letter's rule rather than the record's own, so the
 * screen and the January statement agree. `donor_key` says which key that was.
 */
export interface GivingHistory {
  donor_key: string | null;
  gifts: number;
  total_cents: number;
  deductible_cents: number;
  first_gift_at: string | null;
  last_gift_at: string | null;
  years: GivingYear[];
  best_year: GivingYear | null;
  consecutive_years: number;
  recurring: boolean;
}

export interface CustomerDetailResponse {
  summary: CustomerSummaryRow & { attendance_pct: number | null; no_shows: number; exchanges: number; transfers_out: number; lifetime_ticket_sales_cents?: number; payments_received_cents?: number; outstanding_balance_cents?: number; past_due_balance_cents?: number; open_orders?: number };
  orders: Record<string, unknown>[];
  events: { event_id: string; title: string; performances: number; tickets: number; admitted: number; last_starts_at: string | null; spend_cents: number }[];
  donations: Record<string, unknown>[];
  packages: Record<string, unknown>[];
  passes: Record<string, unknown>[];
  ticket_types: { ticket_type: string | null; tickets: number }[];
  channels: { channel: string; orders: number; spend_cents: number }[];
  giving: GivingHistory;
  history: { orders: HistoricalOrder[]; totals: HistoryTotals };
}

/** Notes and tags on a customer (D-358). */
export interface CustomerTagBadge {
  code: string;
  name: string;
  warns_at_sale: boolean;
}

export interface CustomerTag extends CustomerTagBadge {
  assignment_id: string;
  assigned_at: string;
  assigned_by_name: string | null;
  note: { id: string; body: string; author_name: string | null; created_at: string } | null;
}

export interface CustomerNote {
  id: string;
  body: string;
  visibility: string;
  author_name: string | null;
  created_at: string;
  tag_name: string | null;
}

export interface CustomerTagHistoryRow {
  assignment_id: string;
  code: string;
  name: string;
  assigned_at: string;
  assigned_by_name: string | null;
  removed_at: string | null;
  removed_by_name: string | null;
  note_body: string | null;
}

export interface CustomerTagKind {
  code: string;
  name: string;
  warns_at_sale: number;
  active: number;
  sort_order: number;
}

/** What somebody selling to a person should know first (D-358). */
export interface CustomerAtSale {
  notes_count: number;
  latest_note: { body: string; author_name: string | null; created_at: string } | null;
  tags: CustomerTag[];
  warnings: { tag_code: string; name: string; note: { body: string; author_name: string | null; created_at: string } | null }[];
}

/** A customer's account credit (D-372). */
export interface CustomerCreditResponse {
  credit: { customer_id: string; email: string | null; name: string | null; verified: boolean; balance_cents: number; spendable_cents: number } | null;
  entries: { id: string; kind: 'ISSUED' | 'REDEEMED' | 'REVERSED' | 'ADJUSTED' | 'EXPIRED'; kind_name: string; amount_cents: number; balance_after_cents: number; created_at: string; order_id: string | null; order_number: string | null; refund_number: string | null; reason: string | null; actor_type: string }[];
  policy: { expires_after_days: number | null; staff_adjustments: boolean };
}

export const customerEndpoints = {
  search: (q: string, tag?: string) => api.get<{ customers: CustomerSummaryRow[] }>(`/api/admin/customers?q=${encodeURIComponent(q)}${tag ? `&tag=${encodeURIComponent(tag)}` : ''}`),
  detail: (key: string) => api.get<CustomerDetailResponse>(`/api/admin/customers/${encodeURIComponent(key)}`),
  record: (key: string) => api.get<{ notes: CustomerNote[]; tags: CustomerTag[]; tag_history: CustomerTagHistoryRow[] }>(`/api/admin/customers/${encodeURIComponent(key)}/record`),
  addNote: (key: string, body: string) => api.post<{ note: CustomerNote }>(`/api/admin/customers/${encodeURIComponent(key)}/notes`, { body }),
  assignTag: (key: string, input: { tag_code: string; note?: string | null }) => api.post<{ tags: CustomerTag[] }>(`/api/admin/customers/${encodeURIComponent(key)}/tags`, input),
  removeTag: (key: string, code: string, note?: string | null) => api.post<{ tags: CustomerTag[] }>(`/api/admin/customers/${encodeURIComponent(key)}/tags/${encodeURIComponent(code)}/remove`, { note: note ?? null }),
  /** Account credit (D-372): balance, entries newest first, and the policy. */
  credit: (key: string) => api.get<CustomerCreditResponse>(`/api/admin/customers/${encodeURIComponent(key)}/credit`),
  adjustCredit: (key: string, input: { amount_cents: number; reason: string; idempotency_key?: string }) => api.post<CustomerCreditResponse & { entry_id: string; balance_cents: number }>(`/api/admin/customers/${encodeURIComponent(key)}/credit/adjustments`, input),
  tagKinds: (all = false) => api.get<{ tags: CustomerTagKind[] }>(`/api/admin/customers/tags${all ? '?all=1' : ''}`),
  createTagKind: (input: { name: string; warns_at_sale: boolean }) => api.post<{ tag: CustomerTagKind }>('/api/admin/customers/tags', input),
  updateTagKind: (code: string, input: { name?: string; active?: boolean; warns_at_sale?: boolean }) => api.patch<{ tag: CustomerTagKind }>(`/api/admin/customers/tags/${encodeURIComponent(code)}`, input),
  atSale: (subject: { sub?: string | null; email?: string | null }) => {
    const params = new URLSearchParams();
    if (subject.sub) params.set('sub', subject.sub);
    if (subject.email) params.set('email', subject.email);
    return api.get<{ record: CustomerAtSale }>(`/api/admin/customers/at-sale?${params}`);
  },
};

export const templateEndpoints = {
  list: () => api.get<{ templates: TicketTemplateRow[] }>('/api/admin/ticket-templates'),
  create: (input: Record<string, unknown>) => api.post<{ template: TicketTemplateRow }>('/api/admin/ticket-templates', input),
  update: (id: string, input: Record<string, unknown>) => api.patch<{ template: TicketTemplateRow }>(`/api/admin/ticket-templates/${id}`, input),
  events: (id: string) => api.get<{ events: { id: string; slug: string; title: string; status: string }[] }>(`/api/admin/ticket-templates/${id}/events`),
  assign: (id: string, eventId: string) => api.put(`/api/admin/ticket-templates/${id}/events/${eventId}`, {}),
  unassign: (id: string, eventId: string) => api.del(`/api/admin/ticket-templates/${id}/events/${eventId}`),
  uploadImage: async (id: string, kind: 'sponsor-logo' | 'header' | 'wallet-logo' | 'wallet-hero', file: File) => {
    const response = await fetch(`/api/admin/ticket-templates/${id}/${kind}`, { method: 'PUT', credentials: 'same-origin', headers: { 'content-type': file.type, 'x-bre-csrf': '1', accept: 'application/json' }, body: file });
    if (!response.ok) throw await toError(response);
    return (await response.json()) as { template: TicketTemplateRow };
  },
  removeImage: (id: string, kind: 'sponsor-logo' | 'header' | 'wallet-logo' | 'wallet-hero') => api.del<{ template: TicketTemplateRow }>(`/api/admin/ticket-templates/${id}/${kind}`),
  imageUrl: (id: string, kind: 'sponsor-logo' | 'header' | 'wallet-logo' | 'wallet-hero') => `/api/public/ticket-templates/${id}/${kind}`,
};

/** D-72: the refund picture for a canceled performance. */
export interface PerformanceCancellation {
  performance_id: string;
  status: string;
  cancelled_at: string | null;
  cancellation_reason: string | null;
  orders_outstanding: number;
  tickets_outstanding: number;
  outstanding_value_cents: number;
  orders_refunded: number;
  tickets_refunded: number;
  refunded_cents: number;
  package_instances: number;
  failed_refunds: { order_id: string; order_number: string; refund_number: string; status: string; message: string | null }[];
}

export interface StorefrontBranding {
  logo_header_url?: string | null;
  logo_footer_url?: string | null;
  logo_icon_url?: string | null;
  hero_image_url?: string | null;
  logo_header_file_id: string | null;
  logo_footer_file_id: string | null;
  logo_icon_file_id: string | null;
  hero_image_file_id: string | null;
  logo_alt: string;
  logo_header_height: number;
  logo_footer_height: number;
  show_platform_attribution: boolean;
  texture_enabled: boolean;
  copyright_holder: string | null;
  address: string[];
  phone: string | null;
  email: string | null;
  facebook_url: string | null;
  instagram_url: string | null;
  x_url: string | null;
  youtube_url: string | null;
  /** The team's own words on the storefront (D-367). */
  copy: StorefrontCopy;
  /** The content pages, as Markdown (D-367). */
  content: StorefrontContent;
  /** What the deployment calls what it sells (D-378). Older API responses may lack it. */
  terms?: TermsSetting;
  color_brand: string;
  color_brand_hover: string;
  color_background: string;
  color_surface_muted: string;
  color_accent: string;
}

export interface StorefrontCopy {
  hero_kicker: string | null;
  hero_headline: string | null;
  hero_script: string | null;
  hero_intro: string | null;
  tagline: string | null;
  slogan: string | null;
  cheer: string | null;
}

export interface StorefrontContent {
  about_md: string | null;
  parking_md: string | null;
  seating_md: string | null;
  policies_md: string | null;
  accessibility_md: string | null;
  group_outings_md: string | null;
  privacy_md: string | null;
}

export type BrandingImageKind = 'header' | 'footer' | 'icon' | 'hero';

/** Storefront branding (D-126): logos, colors, contact details. */
export const brandingEndpoints = {
  get: () => api.get<{ branding: StorefrontBranding }>('/api/admin/settings/branding'),
  save: (branding: StorefrontBranding) => api.put<{ branding: StorefrontBranding }>('/api/admin/settings/branding', branding),
  uploadLogo: async (kind: BrandingImageKind, file: File) => {
    const response = await fetch(`/api/admin/settings/branding/logo/${kind}`, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'content-type': file.type, 'x-bre-csrf': '1', accept: 'application/json' },
      body: file,
    });
    if (!response.ok) throw Object.assign(new Error((await response.json().catch(() => ({}))).error?.message ?? 'Upload failed'), { code: 'UPLOAD_FAILED' });
    return (await response.json()) as { file_id: string; branding: StorefrontBranding };
  },
  removeLogo: (kind: BrandingImageKind) => api.del<{ branding: StorefrontBranding }>(`/api/admin/settings/branding/logo/${kind}`),
};

// --- QuickBooks (D-138) -----------------------------------------------------

export interface QboMappingRow {
  id: string;
  accounting_code: string;
  code_name: string;
  family: string;
  normal_balance: string;
  qbo_account_id: string;
  qbo_account_name_snapshot: string;
  qbo_class_id: string | null;
  effective_from: string;
}

export interface QboAccountRow {
  Id: string;
  Name: string;
  AccountType: string;
  AccountSubType?: string;
}

export interface QboBatchRow {
  id: string;
  batch_number: string;
  strategy: string;
  batch_key: string;
  accounting_date: string;
  status: string;
  qbo_doc_number: string;
  qbo_transaction_id: string | null;
  total_debits_cents: number;
  attempts: number;
  last_error: string | null;
  created_at: string;
  posted_at: string | null;
  journals: number;
}

export interface QboOverview {
  environment: string;
  /** False when the Intuit app's secrets are not set on this deployment. */
  configured: boolean;
  connection: { realm_id: string; status: string; connected_at: string; access_token_expires_at: string } | null;
  mappings: QboMappingRow[];
  unmapped: { code: string; name: string; family: string; normal_balance: string }[];
  ready_to_post: boolean;
  batches_by_status: Record<string, { batches: number; total_cents: number }>;
  unbatched_journals: number;
}

export const quickbooksEndpoints = {
  overview: () => api.get<QboOverview>('/api/admin/quickbooks'),
  connect: (redirect_uri: string) => api.post<{ authorize_url: string; state: string }>('/api/admin/quickbooks/connect', { redirect_uri }),
  callback: (input: { code: string; realm_id: string; state: string; redirect_uri: string }) => api.post<{ connection: { realm_id: string } }>('/api/admin/quickbooks/callback', input),
  disconnect: () => api.post<{ disconnected: boolean }>('/api/admin/quickbooks/disconnect', {}),
  accounts: () => api.get<{ accounts: QboAccountRow[] }>('/api/admin/quickbooks/accounts'),
  projects: () => api.get<{ projects: { id: string; name: string; is_project: boolean }[] }>('/api/admin/quickbooks/projects'),
  eventProject: (eventId: string) => api.get<{ project: { qbo_customer_id: string; qbo_project_name_snapshot: string; linked_at: string } | null }>(`/api/admin/quickbooks/events/${eventId}/project`),
  linkEventProject: (eventId: string, qboCustomerId: string) => api.put<{ rebuilt: number }>(`/api/admin/quickbooks/events/${eventId}/project`, { qbo_customer_id: qboCustomerId }),
  unlinkEventProject: (eventId: string) => api.delete<{ rebuilt: number }>(`/api/admin/quickbooks/events/${eventId}/project`),
  saveMapping: (input: { accounting_code: string; qbo_account_id: string }) => api.put<{ mapping_id: string; type_warning: string | null; rebuilt?: number }>('/api/admin/quickbooks/mappings', input),
  batches: (status?: string) => api.get<{ batches: QboBatchRow[] }>(`/api/admin/quickbooks/batches${status ? `?status=${status}` : ''}`),
  batch: (id: string) =>
    api.get<{ batch: QboBatchRow & Record<string, unknown>; lines: Record<string, unknown>[]; items: Record<string, unknown>[]; attempts: Record<string, unknown>[] }>(`/api/admin/quickbooks/batches/${id}`),
  build: (before_date?: string) => api.post<{ built: { batch_number: string; journals: number; missing_mappings: string[] }[] }>('/api/admin/quickbooks/batches/build', before_date ? { before_date } : {}),
  post: (id: string) => api.post<{ status: string; error?: string; adopted?: boolean }>(`/api/admin/quickbooks/batches/${id}/post`, {}),
  rebuild: (id: string) => api.post<{ status: string; lines: number; missing_mappings: string[] }>(`/api/admin/quickbooks/batches/${id}/rebuild`, {}),
  skip: (id: string, reason: string) => api.post<{ skipped: boolean }>(`/api/admin/quickbooks/batches/${id}/skip`, { reason }),
  verify: () => api.post<{ checked: number; drifted: string[] }>('/api/admin/quickbooks/verify', {}),
};

// --- Presales (D-141) --------------------------------------------------------

export interface PresaleRow {
  id: string;
  /** Null means every performance of the event. */
  performance_id: string | null;
  /** The operator's identifier for this presale, not a customer credential. */
  code: string;
  name: string;
  access_type: string;
  starts_at: string;
  ends_at: string;
  ticket_limit_per_customer: number | null;
  active: number;
}

export const presaleEndpoints = {
  list: (eventId: string) => api.get<{ presales: PresaleRow[] }>(`/api/admin/events/${eventId}/presales`),
  create: (eventId: string, input: Record<string, unknown>) => api.post<{ presale: PresaleRow }>(`/api/admin/events/${eventId}/presales`, input),
  update: (eventId: string, presaleId: string, input: Record<string, unknown>) => api.patch<{ presale: PresaleRow }>(`/api/admin/events/${eventId}/presales/${presaleId}`, input),
};

// --- Promotions (D-143) ------------------------------------------------------

export interface PromotionRow {
  id: string;
  code: string;
  name: string;
  public_description: string | null;
  promotion_type: string;
  status: string;
  eligibility_mode: string;
  event_id: string | null;
  unlocks_presale_id: string | null;
  starts_at: string | null;
  ends_at: string | null;
  discount_basis_points: number | null;
  discount_amount_cents: number | null;
  /** 'PER_ORDER' takes one amount off the order rather than each ticket (D-163). */
  discount_scope?: 'PER_TICKET' | 'PER_ORDER';
  fixed_price_cents: number | null;
  buy_quantity: number | null;
  get_quantity: number | null;
  get_discount_basis_points: number | null;
  min_quantity: number | null;
  max_quantity_per_order: number | null;
  usage_cap_total: number | null;
  usage_cap_per_customer: number | null;
  funded_by_owner_type: string;
  funded_by_organization_id: string | null;
  accounting_code: string;
  stackable: number;
  /** An automatic ticket deal of a library promotion (D-377): no codes, managed on the Promotions page. */
  automatic?: number;
  created_at: string;
  /** Only on the list: how many codes it has, and how many tickets it has given away. */
  codes?: number;
  used?: number;
}

export interface PromoCodeRow {
  id: string;
  code_display: string;
  code_normalized: string;
  usage_cap: number | null;
  active: number;
  created_at: string;
  used: number;
}

/** A batch of single-use codes (D-359). */
export interface PromoCodeBatch {
  id: string;
  promotion_id: string;
  name: string;
  prefix: string;
  code_length: number;
  quantity: number;
  uses_per_code: number;
  created_at: string;
  disabled_at: string | null;
  codes: number;
  used_codes: number;
  disabled_codes: number;
  in_checkout: number;
}

export interface PromoBatchCode {
  id: string;
  code: string;
  recipient_name: string | null;
  recipient_email: string | null;
  uses_allowed: number;
  used: number;
  in_checkout: number;
  active: number;
  status: 'UNUSED' | 'IN_CHECKOUT' | 'PARTLY_USED' | 'USED' | 'DISABLED';
  order_numbers: string[];
}

export interface PromotionRestrictions {
  ticket_type_ids: string[];
  /** By code, across games (D-377): "GA" on every game a season build copied. */
  ticket_type_codes: string[];
  price_zone_codes: string[];
  section_ids: string[];
  price_zone_ids: string[];
  performance_ids: string[];
  channels: string[];
  customer_subs: string[];
}

export const promotionEndpoints = {
  list: (query: { event_id?: string; status?: string; automatic?: '0' | '1' } = {}) => {
    const params = new URLSearchParams(Object.entries(query).filter(([, value]) => Boolean(value)) as [string, string][]);
    return api.get<{ promotions: PromotionRow[] }>(`/api/admin/promotions${params.size ? `?${params}` : ''}`);
  },
  get: (id: string) =>
    api.get<{ promotion: PromotionRow; codes: PromoCodeRow[]; restrictions: PromotionRestrictions; usage: { counter_key: string; cap_quantity: number | null; reserved_quantity: number; committed_quantity: number }[] }>(`/api/admin/promotions/${id}`),
  create: (input: Record<string, unknown>) => api.post<{ promotion: PromotionRow }>('/api/admin/promotions', input),
  update: (id: string, input: Record<string, unknown>) => api.patch<{ promotion: PromotionRow }>(`/api/admin/promotions/${id}`, input),
  setRestrictions: (id: string, input: Partial<PromotionRestrictions>) => api.put<{ restrictions: PromotionRestrictions }>(`/api/admin/promotions/${id}/restrictions`, input),
  createCode: (id: string, input: { code: string; usage_cap?: number | null }) => api.post<{ code: PromoCodeRow }>(`/api/admin/promotions/${id}/codes`, input),
  updateCode: (id: string, codeId: string, input: { active?: boolean; usage_cap?: number | null }) => api.patch<{ code: PromoCodeRow }>(`/api/admin/promotions/${id}/codes/${codeId}`, input),
  redemptions: (id: string) => api.get<{ redemptions: Record<string, unknown>[] }>(`/api/admin/promotions/${id}/redemptions`),
  /** Batches of single-use codes (D-359). */
  batches: (id: string) => api.get<{ batches: PromoCodeBatch[] }>(`/api/admin/promotions/${id}/batches`),
  createBatch: (id: string, input: { name: string; quantity?: number; prefix: string; length: number; uses_per_code: number; recipients?: { name: string | null; email: string | null }[] }) =>
    api.post<{ batch: PromoCodeBatch }>(`/api/admin/promotions/${id}/batches`, input),
  batch: (id: string, batchId: string) => api.get<{ batch: PromoCodeBatch; codes: PromoBatchCode[] }>(`/api/admin/promotions/${id}/batches/${batchId}`),
  disableBatch: (id: string, batchId: string) => api.post<{ batch: PromoCodeBatch }>(`/api/admin/promotions/${id}/batches/${batchId}/disable`),
  batchCsvUrl: (id: string, batchId: string) => `/api/admin/promotions/${id}/batches/${batchId}/codes.csv`,
  /** What a code's restrictions can name: upcoming games, and the ticket type and zone codes in use (D-377). */
  restrictionChoices: () =>
    api.get<{ performances: { id: string; event_id: string; title: string; starts_at: string; timezone: string }[]; ticket_type_codes: { code: string; name: string }[]; price_zone_codes: { code: string; name: string }[]; channels: string[] }>(
      '/api/admin/promotions/restriction-choices',
    ),
};

/**
 * The audit log (D-195).
 *
 * The API answers exactly one anchor at a time, because each is a separate
 * index and a combination is served by none of them. The client mirrors that
 * shape rather than offering a free-form filter object, so an unanswerable
 * query cannot be assembled here and refused there.
 */
export interface AuditEntry {
  id: string;
  occurred_at: string;
  action: string;
  actor_type: string;
  actor_id: string | null;
  actor_display_snapshot: string | null;
  approved_by_staff_account_id: string | null;
  session_id: string | null;
  auth_method: string | null;
  surface: string | null;
  device_id: string | null;
  register_id: string | null;
  ip_address: string | null;
  user_agent: string | null;
  request_id: string | null;
  target_type: string;
  target_id: string;
  event_id: string | null;
  performance_id: string | null;
  order_id: string | null;
  reason: string | null;
  before_json: string | null;
  after_json: string | null;
}

export type AuditAnchor =
  | { kind: 'recent' }
  | { kind: 'target'; target_type: string; target_id: string }
  | { kind: 'actor'; actor_id: string }
  | { kind: 'action'; action: string }
  | { kind: 'event'; event_id: string }
  | { kind: 'order'; order_id: string };

export interface AuditCursor {
  before_at: string;
  before_id: string;
}

export const auditEndpoints = {
  list: (anchor: AuditAnchor, window: { from?: string; to?: string } = {}, cursor: AuditCursor | null = null, limit = 100) => {
    const query = new URLSearchParams();
    if (anchor.kind === 'target') {
      query.set('target_type', anchor.target_type);
      query.set('target_id', anchor.target_id);
    } else if (anchor.kind === 'actor') query.set('actor_id', anchor.actor_id);
    else if (anchor.kind === 'action') query.set('action', anchor.action);
    else if (anchor.kind === 'event') query.set('event_id', anchor.event_id);
    else if (anchor.kind === 'order') query.set('order_id', anchor.order_id);
    if (window.from) query.set('from', window.from);
    if (window.to) query.set('to', window.to);
    if (cursor) {
      query.set('before_at', cursor.before_at);
      query.set('before_id', cursor.before_id);
    }
    query.set('limit', String(limit));
    return api.get<{ entries: AuditEntry[]; next: AuditCursor | null }>(`/api/admin/audit?${query}`);
  },
};

/** The home screen (D-196): counts that link out, never a second copy of another page. */
export interface DashboardShow {
  id: string;
  event_id: string;
  title: string;
  starts_at: string;
  doors_at: string | null;
  timezone: string;
  status: string;
  venue_name: string | null;
  capacity: number;
  sold: number;
  available: number;
  in_flight: number;
  held: number;
  /** Where the rows behind these numbers are (D-304). */
  sold_drill?: DrillSpec;
  units_drill?: DrillSpec;
}

export interface Dashboard {
  generated_at: string;
  business_date: string;
  timezone: string;
  shows: DashboardShow[];
  sales: {
    today: { orders: number; gross_cents: number; tickets: number; tickets_drill?: DrillSpec; gross_drill?: DrillSpec };
    yesterday: { orders: number; gross_cents: number; gross_drill?: DrillSpec };
  };
  attention: {
    refund_requests: number;
    oldest_refund_request: string | null;
    open_disputes: number;
    soonest_evidence_due: string | null;
  };
  alerts: { id: string; severity: string; code: string; message: string; created_at: string }[];
}

export const dashboardEndpoints = {
  get: () => api.get<Dashboard>('/api/admin/dashboard'),
};
